import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import { IoIosAttach } from 'react-icons/io';
import { IoWarningOutline, IoSearchSharp } from 'react-icons/io5';
import { RxCross2 } from 'react-icons/rx';
import { FiSend } from 'react-icons/fi';
import { RiRobot3Line, RiChatNewLine, RiMenuLine } from 'react-icons/ri';
import { CiLogout } from 'react-icons/ci';
import { FaRegCopy, FaCheck } from 'react-icons/fa6';
import { MdDeleteOutline, MdOutlineHelpOutline, MdOutlineWorkspacePremium } from 'react-icons/md';
import { GoSidebarExpand, GoSidebarCollapse } from 'react-icons/go';
import { PiChatsThin } from 'react-icons/pi';
import { CgSandClock } from 'react-icons/cg';
import { SlLike, SlDislike } from 'react-icons/sl';
import { ReactComponent as DownloadArrowIcon } from '../assets/icons/download-arrow.svg';
import { ReactComponent as UserIcon } from '../assets/icons/user-gender-neutral.svg';
import { ReactComponent as SearchIcon } from '../assets/icons/search-magnifier.svg';
import { authAPI, conversationsAPI, conversationExportAPI, feedbackAPI, fileAPI, certificateAPI } from '../services/api';
import { getDraft, setDraft, getLastDraftId } from '../services/draftStorage';
import { endSession } from '../services/session';
import FileUpload from './FileUpload';
import LanguageToggle, { getLang, setLang } from './LanguageToggle';
import HelpFeedbackModal from './HelpFeedbackModal';
import './Chat.css';
import './HelpFeedbackModal.css';

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'a', 'b', 'i', 'em', 'strong', 'u', 'br', 'p', 'div', 'span', 'hr',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'blockquote', 'pre', 'code', 'small', 'sup', 'sub',
  ],
  ALLOWED_ATTR: ['class', 'style', 'href', 'title', 'colspan', 'rowspan', 'align'],
  ALLOW_DATA_ATTR: false,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'form', 'input'],
  FORBID_ATTR: ['srcset', 'formaction', 'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus'],
};

const sanitizeHtml = (raw) => DOMPurify.sanitize(raw || '', SANITIZE_CONFIG);

// Wrap matches of `query` within `text` in <mark class="search-highlight"> spans.
// Returns a React node (string when no query, array of strings+marks otherwise).
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const highlightText = (text, query) => {
  const s = String(text ?? '');
  const q = (query || '').trim();
  if (!q || !s) return s;
  const re = new RegExp(escapeRegex(q), 'gi');
  const out = [];
  let last = 0;
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push(s.slice(last, m.index));
    out.push(<mark key={`hl-${m.index}`} className="search-highlight">{m[0]}</mark>);
    last = re.lastIndex;
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  if (last === 0) return s;
  if (last < s.length) out.push(s.slice(last));
  return out;
};

// Flatten a React children tree to its plain-text equivalent. Used to decide
// whether a rendered block contains the search query so we can tag the wrapper
// with `has-search-match`.
const childrenToString = (children) => {
  if (children == null || typeof children === 'boolean') return '';
  if (typeof children === 'string' || typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(childrenToString).join('');
  if (children.props && children.props.children !== undefined) {
    return childrenToString(children.props.children);
  }
  return '';
};

// Walk React children and replace plain-string segments with highlight nodes.
// Non-string children (already-rendered JSX elements) are returned untouched —
// their own rendering goes through the components map and will get their own
// highlight pass.
const highlightChildren = (children, query) => {
  const q = (query || '').trim();
  if (!q || children == null) return children;
  if (typeof children === 'string') return highlightText(children, q);
  if (Array.isArray(children)) {
    return children.map((c, i) => {
      if (typeof c === 'string') {
        const r = highlightText(c, q);
        return Array.isArray(r) ? <React.Fragment key={`s-${i}`}>{r}</React.Fragment> : r;
      }
      return c;
    });
  }
  return children;
};

// Strip backend-appended disclaimer from message content before display.
// The backend streams it as a delayed token; we render it immediately as a UI element instead.
const DISCLAIMER_MARKER = '\n\n⚠️ PRISM provides evidence-based information';
const stripDisclaimer = (text) => {
  if (!text) return text;
  const idx = text.indexOf(DISCLAIMER_MARKER);
  return idx >= 0 ? text.slice(0, idx) : text;
};

// ── Key Points wrapper ─────────────────────────────────────────────────────
// Backend always emits responses inside this structure:
//   <key_points>\n- ...\n</key_points>\n\n<answer>\n...\n</answer>
// splitKeyPointsAndAnswer extracts the two sections so we can render the
// Key Points block in a distinct UI box BEFORE the answer body. The parser
// handles partial tags gracefully (during streaming the closing tags may not
// have arrived yet) and falls back to plain rendering if <key_points> never
// appears (model non-compliance or canned greeting responses).
const KEY_POINTS_OPEN  = '<key_takeaways>';
const KEY_POINTS_CLOSE = '</key_takeaways>';
const ANSWER_OPEN  = '<answer>';
const ANSWER_CLOSE = '</answer>';

const splitKeyPointsAndAnswer = (text) => {
  if (!text) return { keyPoints: '', answerBody: '', hasKeyPoints: false };

  const kpStart = text.indexOf(KEY_POINTS_OPEN);
  if (kpStart < 0) {
    return { keyPoints: '', answerBody: text, hasKeyPoints: false };
  }

  const kpContentStart = kpStart + KEY_POINTS_OPEN.length;
  const kpEnd = text.indexOf(KEY_POINTS_CLOSE, kpContentStart);

  let keyPoints;
  let rest;
  if (kpEnd < 0) {
    // </key_points> hasn't arrived yet — stream into the box live
    keyPoints = text.slice(kpContentStart);
    rest = '';
  } else {
    keyPoints = text.slice(kpContentStart, kpEnd);
    rest = text.slice(kpEnd + KEY_POINTS_CLOSE.length);
  }

  // Strip <answer>...</answer> tags from `rest` but preserve any content that
  // sits outside the tags (e.g. the medical disclaimer, which is appended
  // AFTER </answer> by the backend).
  let answerBody;
  const aOpen = rest.indexOf(ANSWER_OPEN);
  if (aOpen < 0) {
    answerBody = rest;
  } else {
    const aContentStart = aOpen + ANSWER_OPEN.length;
    const aClose = rest.indexOf(ANSWER_CLOSE, aContentStart);
    if (aClose < 0) {
      // </answer> not yet streamed — render everything past <answer>
      answerBody = rest.slice(0, aOpen) + rest.slice(aContentStart);
    } else {
      answerBody = rest.slice(0, aOpen)
                 + rest.slice(aContentStart, aClose)
                 + rest.slice(aClose + ANSWER_CLOSE.length);
    }
  }

  // Defense-in-depth: keyPoints should never itself start with the opening
  // tag. It can if the model echoed the assistant prefill instead of
  // treating it as a true continuation (backend de-dupes this — see
  // dedupe_leading_prefill in rag_service.py — but older persisted messages
  // saved before that fix, or any path that bypasses it, could still have
  // the literal tag here). Strip any leading repeat(s) so it never renders
  // as visible text.
  let cleanKeyPoints = keyPoints;
  while (cleanKeyPoints.replace(/^\s+/, '').startsWith(KEY_POINTS_OPEN)) {
    cleanKeyPoints = cleanKeyPoints.replace(/^\s+/, '').slice(KEY_POINTS_OPEN.length);
  }

  return {
    keyPoints: cleanKeyPoints.replace(/^\s+|\s+$/g, ''),
    answerBody: answerBody.replace(/^\s+|\s+$/g, ''),
    hasKeyPoints: true,
  };
};

// ── Follow-Up Questions extraction ─────────────────────────────────────────
// The LLM produces a "Follow-Up Questions" section near the end of every
// clinical answer (per the system prompt's section X). We surface those as
// clickable chips that load into the input textarea on click, but we still
// render them in the SAME visual position they currently occupy (end of
// the answer body) so the layout doesn't move.
//
// The parser pulls the section out of the body so it isn't double-rendered
// (once as text by ReactMarkdown, once as chips).
//
// Heading variants intentionally supported:
//   ## Follow-Up Questions
//   ## Follow-up Questions:
//   **Follow-Up Questions**
//   **Follow-Up Questions:**     (was the broken case in v1)
//   X. Follow-Up Questions       (the section-numbered heading from the prompt)
//   10. Follow-Up Questions
//   ## X. Follow-Up Questions
//   **X. Follow-Up Questions**
//   Suggested Follow-Up Questions
//   Suggested / Related / Discussion / Recommended / Further / Additional
//   / Next / Possible  Questions
//   With or without trailing colon and any combination of #/* markers.
const FOLLOWUP_HEADING_RE = /(?:^|\n)[ \t]*[#*]*[ \t]*(?:[A-Za-z0-9]{1,4}[.)]\s+)?(?:(?:Suggested|Recommended|Related|Additional|Further|Next|Possible|Discussion)\s+(?:Follow[-\s]?[Uu]p\s+)?|Follow[-\s]?[Uu]p\s+)Questions?\b[ \t]*[:#*]*[ \t]*(?:\n|$)/i;

const _cleanQuestionLine = (raw) => {
  let q = (raw || '').trim();
  if (!q) return '';
  // Strip bullet / numbered list markers ("- ", "* ", "1. ", "2) ")
  q = q.replace(/^(?:[-*•+]|\d+[.)])\s+/, '').trim();
  // Strip surrounding bold/italic markdown
  q = q.replace(/^\*+|\*+$/g, '').replace(/^_+|_+$/g, '').trim();
  // Strip surrounding quotes (straight + curly)
  q = q.replace(/^["'“”‘’]+|["'“”‘’]+$/g, '').trim();
  return q;
};

// Fallback: when no explicit Follow-Up heading is present, look at the tail of
// the body for a contiguous list of bullet/numbered lines that all end with
// "?" — that's almost always a follow-up question list the LLM emitted
// without (or with a non-matching) heading. Requires at least 2 such lines so
// a stray rhetorical question in the body doesn't get hoovered up.
const _extractTrailingQuestionList = (text) => {
  const lines = text.split('\n');
  let i = lines.length - 1;
  // Skip trailing blank lines
  while (i >= 0 && !lines[i].trim()) i--;

  // Collect contiguous bullet/numbered question lines from the bottom up.
  const collected = []; // { content, lineIndex }
  while (i >= 0) {
    const line = lines[i].trim();
    if (!line) {
      // Blank inside the list is allowed only if we already have ≥ 1 questions
      // — many LLMs put a blank line between bullets. Skip and keep walking.
      if (collected.length === 0) break;
      i--;
      continue;
    }
    const bulletMatch = line.match(/^(?:[-*•+]|\d+[.)])\s+(.+)$/);
    if (!bulletMatch) break;
    const content = _cleanQuestionLine(bulletMatch[1]);
    if (content.length < 5 || !content.includes('?')) break;
    collected.unshift({ content, lineIndex: i });
    i--;
  }

  if (collected.length < 2) return null;

  const startLine = collected[0].lineIndex;
  const bodyWithoutSection = lines.slice(0, startLine).join('\n').replace(/\s+$/, '');
  return { body: bodyWithoutSection, questions: collected.map((c) => c.content) };
};

const splitFollowUpQuestions = (text) => {
  if (!text) return { body: text || '', questions: [] };
  const headingMatch = text.match(FOLLOWUP_HEADING_RE);

  if (headingMatch) {
    const headingStart = headingMatch.index;
    const headingEnd = headingMatch.index + headingMatch[0].length;

    // The follow-up section ends at the next major heading or end of text.
    // Major heading = ## ... or **bold-line** on its own line. The list items
    // themselves are bullets/numbers, not headings, so they won't match here.
    const rest = text.slice(headingEnd);
    const nextHeadingMatch = rest.match(/\n[ \t]*(?:#{1,4}\s+\S|\*{2}[^*\n]{2,}\*{2}[ \t]*:?[ \t]*(?:\n|$))/);
    const sectionEnd = nextHeadingMatch ? headingEnd + nextHeadingMatch.index : text.length;

    const sectionText = text.slice(headingEnd, sectionEnd);
    const questions = [];
    for (const rawLine of sectionText.split('\n')) {
      const q = _cleanQuestionLine(rawLine);
      // Heading already confirmed — trust any line containing "?" so we don't
      // accidentally drop short bullets the LLM emitted.
      if (q && q.includes('?')) questions.push(q);
    }

    // Strip the section from the rendered body regardless of how many questions
    // have parsed so far. This avoids a brief flicker during streaming where
    // raw "## Follow-Up Questions\n..." text would otherwise be visible before
    // the first "?" line arrives and the chips replace it.
    const bodyWithoutSection = (text.slice(0, headingStart).replace(/\s+$/, '') + text.slice(sectionEnd))
      .replace(/^\s+/, '');
    return { body: bodyWithoutSection, questions };
  }

  // No explicit heading — try the trailing-list fallback. Catches responses
  // where the LLM emitted the questions but skipped the heading (common on
  // follow-up turns when the model trims structure).
  const trailing = _extractTrailingQuestionList(text);
  if (trailing) return trailing;

  return { body: text, questions: [] };
};

// AssistantContent — renders an assistant message with a Key Points box at the
// top (if the wrapper is present) followed by the answer body. Falls back to
// plain ReactMarkdown rendering if the wrapper is absent.
const AssistantContent = ({ text, components, heading, isFinal, followUpHeading, onFollowUpClick }) => {
  const clean = stripDisclaimer(text || '');
  const { keyPoints, answerBody, hasKeyPoints } = splitKeyPointsAndAnswer(clean);

  // Pull the follow-up questions out of whichever body text we'll render so
  // they aren't shown as text AND chips. During streaming the closing tag may
  // not have arrived; if no Follow-Up heading is found yet, splitFollowUpQuestions
  // just returns the original body and an empty list (no chips rendered).
  const rawBody = hasKeyPoints ? answerBody : clean;
  const { body: renderedBody, questions: followUpQuestions } = splitFollowUpQuestions(rawBody);

  const renderFollowUpChips = () => {
    if (!followUpQuestions.length) return null;
    return (
      <div className="follow-up-questions" role="region" aria-label={followUpHeading || 'Follow-up questions'}>
        <div className="follow-up-heading">{followUpHeading || 'Follow-Up Questions'}</div>
        <div className="follow-up-chips">
          {followUpQuestions.map((q, i) => (
            <button
              key={`${i}-${q.slice(0, 30)}`}
              type="button"
              className="follow-up-chip"
              onClick={() => onFollowUpClick && onFollowUpClick(q)}
              title="Click to insert into the message box"
            >
              {q}
            </button>
          ))}
        </div>
      </div>
    );
  };

  if (!hasKeyPoints) {
    if (isFinal && clean.trim().length > 50) {
      // Conversational follow-ups (clarification / definition asks) come
      // back as plain prose by design — the backend's conversational mode
      // deliberately omits the wrapper. Log at debug so legitimate plain
      // renders don't spam the console.
      // eslint-disable-next-line no-console
      console.debug('PRISM: assistant response missing <key_points> wrapper — rendering as plain text');
    }
    return (
      <>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{renderedBody}</ReactMarkdown>
        {renderFollowUpChips()}
      </>
    );
  }

  return (
    <>
      <div className="key-points-box" role="region" aria-label={heading}>
        <div className="key-points-heading">{heading}</div>
        <div className="key-points-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{keyPoints}</ReactMarkdown>
        </div>
      </div>
      {renderedBody && (
        <div className="answer-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{renderedBody}</ReactMarkdown>
        </div>
      )}
      {renderFollowUpChips()}
    </>
  );
};

// ── Feature 8: Translations ────────────────────────────────────────────────────
// The backend seeds every new conversation row with this English placeholder.
// title (see chat.py init_conversation) until the LLM-generated title lands, so
// it arrives from /init and from any list reload. Titles are localized at render
// time via displayTitle() — the sentinel itself must stay byte-identical to the
// backend string.
const TITLE_PLACEHOLDER = 'Generating title…';

const TRANSLATIONS = {
  en: {
    chats: 'Recent Chats',
    search_placeholder: 'Search chats...',
    loading: 'Loading...',
    no_matching: 'No matching conversations',
    no_conversations: 'No conversations yet',
    today: 'Today',
    yesterday: 'Yesterday',
    last_7_days: 'Last 7 Days',
    last_30_days: 'Last 30 Days',
    older: 'Older',
    new_chat: 'New Chat',
    generating_title: 'Generating title…',
    phi_warning: 'NO PHI: Do not enter personal health information.',
    disclaimer: 'DISCLAIMER: THCC PRISM is for education and research only. Please always apply your professional clinical judgment.',
    welcome: 'Welcome to THCC PRISM!',
    input_placeholder: 'Ask a clinical educational question, discuss a patient case, or develop a treatment plan...',
    input_placeholder_file: 'Ask a question about your uploaded file...',
    input_hint: 'Press Enter to send · Shift+Enter for new line',
    references: 'References',
    hide: 'Hide',
    show: 'Show',
    copy: 'Copy',
    copied: 'Copied!',
    download: 'Download',
    download_txt: 'Download as .txt',
    download_pdf: 'Save as PDF',
    open_source: 'Open source ↗',
    loading_url: 'Loading…',
    helpful: 'Mark as helpful',
    remove_helpful: 'Remove helpful rating',
    not_helpful: 'Mark as not helpful',
    remove_not_helpful: 'Remove unhelpful rating',
    feedback_thanks_positive: '👍 Thanks for your feedback!',
    feedback_thanks_negative: '👎 Thanks for your feedback!',
    feedback_not_available: 'Feedback not available',
    provide_feedback: '👎 Provide Feedback',
    feedback_prompt: 'Help us improve! Please tell us what went wrong with this response:',
    feedback_placeholder: 'Optional: Describe what was incorrect, unhelpful, or could be improved...',
    cancel: 'Cancel',
    submit_feedback: 'Submit Feedback',
    delete_confirm: 'Are you sure you want to delete this conversation?',
    logout: 'Logout',
    feedback_error: 'Failed to submit feedback. Please try again.',
    file_not_found: 'File not found:',
    error_opening: 'Error opening reference:',
    type_label: 'Type:',
    page_label: 'Page:',
    pages_label: 'Pages:',
    guideline_label: 'Guideline:',
    canadian: 'Canadian',
    non_canadian: 'Non-Canadian',
    journal_label: 'Journal:',
    reputable_journal: 'Reputable Journal',
    source_label: 'Source:',
    ai_source: 'AI Source',
    strength_label: 'Strength:',
    detailed: 'Detailed',
    quick_read: 'Quick Read',
    medical_disclaimer: 'THCC PRISM provides evidence-based information for educational purposes only. This information does not replace professional clinical judgment. Always consider individual patient factors and apply your clinical expertise when making clinical decisions. Note: Genetics is a broad and rapidly evolving area. PRISM does not address all genetics-related inquiries; please consult the Genetics Team at genetics@telus.com, as needed.',
    delete: 'Delete',
    dismiss: 'Dismiss',
    close: 'Close',
    send: 'Send',
    response_style: 'Select response style',
    attach_file: 'Attach a PDF, JPG, or PNG file',
    file_label: 'File',
    source_not_openable: 'Source from model knowledge (not openable)',
    click_open_website: 'Click to open website',
    click_open_file: 'Click to open file',
    key_points: 'Key Takeaways',
    follow_up_questions: 'Follow-Up Questions',
    support: 'Support',
    collapse_sidebar: 'Collapse sidebar',
    expand_sidebar: 'Expand sidebar',
    open_user_menu: 'Open user menu',
    recent_chats: 'Recent chats',
    cme_certificate: 'CME Certificate',
    cme_modal_title: 'Generate Continuing Medical Education (CME) Certificate',
    cme_modal_blurb: 'PRISM totals your reading time from every question and answer in your account and issues a self-directed-learning certificate. These hours are non-certified and unaccredited; confirm acceptance with your regulatory college.',
    cme_professional_designation: 'Professional Designation',
    cme_registration_number: 'Registration / License Number',
    cme_optional_hint: 'Optional',
    cme_period_label: 'Time Period',
    cme_period_hint: 'Choose which time range to count toward your certificate. Leave it as "All time" to include every interaction from your first message until now.',
    cme_period_all: 'All time (default)',
    cme_period_last_7: 'Last 7 days',
    cme_period_last_30: 'Last 30 days',
    cme_period_last_90: 'Last 90 days',
    cme_period_last_365: 'Last 12 months',
    cme_period_custom: 'Custom date range',
    cme_period_from: 'From',
    cme_period_to: 'To',
    cme_period_error_invalid: 'Please enter a valid date range — the "From" date must be on or before the "To" date.',
    cme_generate: 'Generate Certificate',
    cme_generating: 'Generating…',
    cme_error_generic: 'Could not generate the certificate. Please try again.',
    cme_error_no_activity: 'No chat activity found in the selected period — pick a wider range or start a new conversation.',
    help: 'Help',
    draft_restore_prompt: 'You have an unsent draft for this chat. Restore it?',
    draft_restore_restore: 'Restore',
    draft_restore_discard: 'Discard',
  },
  fr: {
    chats: 'Conversations récentes',
    search_placeholder: 'Rechercher...',
    loading: 'Chargement...',
    no_matching: 'Aucune conversation trouvée',
    no_conversations: 'Aucune conversation',
    today: "Aujourd'hui",
    yesterday: 'Hier',
    last_7_days: '7 derniers jours',
    last_30_days: '30 derniers jours',
    older: 'Plus ancien',
    new_chat: 'Nouvelle conversation',
    generating_title: 'Génération du titre…',
    phi_warning: 'SANS RPS: Ne saississez aucun renseignements personnels sur la santé.',
    disclaimer: "AVERTISSEMENT : THCC PRISM est destiné à l'éducation et à la recherche uniquement. Appliquez toujours votre jugement clinique professionnel.",
    welcome: 'Bienvenue dans THCC PRISM!',
    input_placeholder: "Posez une question pédagogique clinique, discutez d'un cas patient ou élaborez un plan de traitement...",
    input_placeholder_file: 'Posez une question sur votre fichier téléchargé...',
    input_hint: 'Entrée pour envoyer · Maj+Entrée pour nouvelle ligne',
    references: 'Références',
    hide: 'Masquer',
    show: 'Afficher',
    copy: 'Copier',
    copied: 'Copié!',
    download: 'Télécharger',
    download_txt: 'Télécharger en .txt',
    download_pdf: 'Enregistrer en PDF',
    open_source: 'Ouvrir la source ↗',
    loading_url: 'Chargement…',
    helpful: 'Marquer comme utile',
    remove_helpful: 'Retirer la note utile',
    not_helpful: 'Marquer comme non utile',
    remove_not_helpful: 'Retirer la note non utile',
    feedback_thanks_positive: '👍 Merci pour votre retour!',
    feedback_thanks_negative: '👎 Merci pour votre retour!',
    feedback_not_available: 'Retour non disponible',
    provide_feedback: '👎 Soumettre une rétroaction',
    feedback_prompt: "Aidez-nous à nous améliorer! Veuillez nous dire ce qui n'a pas fonctionné avec cette réponse :",
    feedback_placeholder: 'Optionnel: Décrivez ce qui était incorrect, inutile ou pourrait être amélioré...',
    cancel: 'Annuler',
    submit_feedback: 'Soumettre',
    delete_confirm: 'Êtes-vous sûr de vouloir supprimer cette conversation?',
    logout: 'Déconnexion',
    feedback_error: "Échec de l'envoi du retour. Veuillez réessayer.",
    file_not_found: 'Fichier introuvable:',
    error_opening: "Erreur lors de l'ouverture de la référence:",
    type_label: 'Type :',
    page_label: 'Page :',
    pages_label: 'Pages :',
    guideline_label: 'Directive :',
    canadian: 'Canadienne',
    non_canadian: 'Non canadienne',
    journal_label: 'Journal :',
    reputable_journal: 'Journal réputé',
    source_label: 'Source :',
    ai_source: 'Source IA',
    strength_label: 'Force :',
    detailed: 'Détaillé',
    quick_read: 'Lecture rapide',
    medical_disclaimer: "THCC PRISM fournit de l'information factuelle à des fins éducatives uniquement. Cette information ne remplace pas le jugement clinique professionnel. Tenez toujours compte des facteurs propres à chaque patient et appliquez votre expertise clinique dans vos décisions. Remarque : la génétique est un domaine vaste et en rapide évolution. PRISM ne répond pas à toutes les questions génétiques; veuillez consulter l'équipe de génétique à genetics@telus.com au besoin.",
    delete: 'Supprimer',
    dismiss: 'Fermer',
    close: 'Fermer',
    send: 'Envoyer',
    response_style: 'Sélectionner le style de réponse',
    attach_file: 'Joindre un fichier PDF, JPG ou PNG',
    file_label: 'Fichier',
    source_not_openable: 'Source issue du modèle (non ouvrable)',
    click_open_website: 'Cliquer pour ouvrir le site web',
    click_open_file: 'Cliquer pour ouvrir le fichier',
    key_points: 'Points clés à retenir',
    follow_up_questions: 'Questions de suivi',
    support: 'Soutien',
    collapse_sidebar: 'Réduire la barre latérale',
    expand_sidebar: 'Développer la barre latérale',
    open_user_menu: 'Ouvrir le menu utilisateur',
    recent_chats: 'Conversations récentes',
    cme_certificate: 'Certificat FMC',
    cme_modal_title: 'Générer un certificat de formation médicale continue (FMC)',
    cme_modal_blurb: "PRISM calcule votre temps de lecture à partir de toutes vos questions et réponses, puis émet un certificat d'apprentissage autonome. Ces heures ne sont ni certifiées ni accréditées; veuillez confirmer leur acceptation auprès de votre ordre professionnel.",
    cme_professional_designation: 'Désignation professionnelle',
    cme_registration_number: "Numéro d'inscription / de permis",
    cme_optional_hint: 'Facultatif',
    cme_period_label: 'Période',
    cme_period_hint: "Choisissez la période à prendre en compte pour votre certificat. Laissez « Toute la période » pour inclure toutes les interactions depuis votre premier message jusqu'à maintenant.",
    cme_period_all: 'Toute la période (par défaut)',
    cme_period_last_7: '7 derniers jours',
    cme_period_last_30: '30 derniers jours',
    cme_period_last_90: '90 derniers jours',
    cme_period_last_365: '12 derniers mois',
    cme_period_custom: 'Plage personnalisée',
    cme_period_from: 'Du',
    cme_period_to: 'Au',
    cme_period_error_invalid: "Plage de dates invalide — la date « Du » doit précéder ou égaler la date « Au ».",
    cme_generate: 'Générer le certificat',
    cme_generating: 'Génération en cours…',
    cme_error_generic: "Impossible de générer le certificat. Veuillez réessayer.",
    cme_error_no_activity: "Aucune activité de conversation détectée dans la période sélectionnée — élargissez la plage ou commencez une nouvelle conversation.",
    help: 'Aide',
    draft_restore_prompt: 'Vous avez une ébauche non envoyée pour cette conversation. La restaurer?',
    draft_restore_restore: 'Restaurer',
    draft_restore_discard: 'Supprimer',
  },
};

// ── Reference strength derivation ──────────────────────────────────────────
// Maps each Type label onto the evidence-strength bucket displayed next to
// the citation tooltip (and used by the references panel). Kept as a simple
// keyword check so light wording drift ("RCT", "case-control", etc.) still
// resolves correctly.
//
//   Internal guidance / THCC-prioritized external guidance / External Guideline →
//       Unconfirmed - refer to source
//   THCC-prioritized emerging research / Case Series / Reports / Expert Opinion → Emerging
//   Systematic Reviews & Meta-analyses / Randomized Controlled Trials → High
//   Cohort / Case-Control Studies                                     → Medium
//   Unknown                                                            → Unknown
const STRENGTH_KEY_BY_TYPE = (type) => {
  if (!type || !type.trim()) return null;
  const v = type.trim().toLowerCase();
  if (v.includes('emerging'))                              return 'emerging';
  if (v.includes('internal') || v.includes('guidance'))    return 'unconfirmed';
  if (v.includes('external') || v.includes('guideline'))   return 'unconfirmed';
  if (v.includes('systematic') || v.includes('meta'))      return 'high';
  if (v.includes('random') || v === 'rct')                 return 'high';
  if (v.includes('cohort'))                                return 'medium';
  if (v.includes('case-control') || v.includes('case control')) return 'medium';
  if (v.includes('case series') || v.includes('case report') || v.includes('case-series') || v.includes('case-report')) return 'emerging';
  if (v.includes('expert') || v.includes('opinion'))       return 'emerging';
  if (v.includes('unknown'))                               return 'unknown';
  return null;
};

const STRENGTH_LABEL = {
  en: {
    unconfirmed: 'Unconfirmed — refer to source',
    high:        'High',
    medium:      'Medium',
    emerging:    'Emerging',
    unknown:     'Unknown',
  },
  fr: {
    unconfirmed: 'Non confirmé — voir la source',
    high:        'Élevée',
    medium:      'Moyenne',
    emerging:    'Émergente',
    unknown:     'Inconnue',
  },
};

// Strength label localized to the current UI language.
const strengthLabel = (key, lang) =>
  key ? (STRENGTH_LABEL[lang === 'fr' ? 'fr' : 'en'][key] || '') : '';

// ── Reference Type / Source label localization ─────────────────────────────
// Backend emits the user-facing Type strings (and the "Attached File" source
// tag) in English regardless of UI language — the LLM is instructed to use the
// EXACT English category strings so STRENGTH_KEY_BY_TYPE classification stays
// stable. We localize the *display* only, keyed on the normalized English
// string; strength derivation still runs on the original English value.
const TYPE_FR = {
  'external guideline': 'Ligne directrice externe',
  'external guideline (canadian)': 'Ligne directrice externe (canadienne)',
  'external guideline (international)': 'Ligne directrice externe (internationale)',
  'internal guideline': 'Ligne directrice interne',
  'internal guidance': 'Orientation interne',
  'systematic review / meta-analysis': 'Revue systématique / méta-analyse',
  'systematic review & meta-analysis': 'Revue systématique et méta-analyse',
  'randomized controlled trial': 'Essai contrôlé randomisé',
  'cohort study': 'Étude de cohorte',
  'case-control study': 'Étude cas-témoins',
  'case series / report': 'Série de cas / rapport de cas',
  'expert opinion': "Opinion d'expert",
  'unknown': 'Inconnu',
};

const localizeType = (type, lang) => {
  if (!type) return type;
  if (lang !== 'fr') return type;
  return TYPE_FR[type.trim().toLowerCase()] || type;
};

const METATAG_FR = {
  'attached file': 'Fichier joint',
  'attached document': 'Document joint',
  'ai source': 'Source IA',
};

const localizeMetaTag = (tag, lang) => {
  if (!tag) return tag;
  if (lang !== 'fr') return tag;
  return METATAG_FR[tag.trim().toLowerCase()] || tag;
};

// ── Feature 6: Citation tooltip (hover preview with signed URL) ────────────────
const CitationTooltip = ({ refData, onGetSignedUrl, tFn }) => {
  const [url, setUrl] = useState(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const fetchedRef = useRef(false);

  // Viewport-aware positioning.  Default = above + centered; we measure once
  // after layout and flip to below / shift horizontally if the popup would
  // be clipped by the window edges (e.g. citation near the top under the
  // app header bar, or close to the right edge of the message column).
  const popupRef = useRef(null);
  const adjustedRef = useRef(false);
  const [direction, setDirection] = useState('above');         // 'above' | 'below'
  const [hOffset, setHOffset] = useState(0);                   // px shift from center

  useEffect(() => {
    if (!refData || fetchedRef.current || refData.clickable === false) return;
    fetchedRef.current = true;
    if (refData.is_web_result && refData.url) {
      setUrl(refData.url);
      return;
    }
    setLoadingUrl(true);
    onGetSignedUrl(refData.source)
      .then(signedUrl => { if (signedUrl) setUrl(signedUrl); })
      .catch(() => {})
      .finally(() => setLoadingUrl(false));
  }, [refData, onGetSignedUrl]);

  // useLayoutEffect so the position adjustment happens before paint — no
  // visible jump from the default position to the corrected one.
  useLayoutEffect(() => {
    if (adjustedRef.current) return;
    const popup = popupRef.current;
    if (!popup) return;
    const wrapper = popup.parentElement;
    if (!wrapper) return;
    adjustedRef.current = true;

    // Find the bounding container — the chat panel right of the sidebar.
    // Falls back to the messages scroll container, then to the viewport.
    // This is what makes the popup respect the sidebar's right edge instead
    // of slipping behind it when the citation is on the left side of the
    // message column.
    const findBoundingContainer = (el) => {
      let node = el;
      while (node && node !== document.body) {
        if (node.classList && (
              node.classList.contains('chat-main') ||
              node.classList.contains('messages-container'))) {
          return node;
        }
        node = node.parentElement;
      }
      // Last-resort fallback: nearest scrollable ancestor.
      node = el.parentElement;
      while (node && node !== document.body) {
        const ov = window.getComputedStyle(node).overflow;
        if (ov === 'auto' || ov === 'scroll') return node;
        node = node.parentElement;
      }
      return null;
    };

    const wrapperRect = wrapper.getBoundingClientRect();
    const popupWidth  = popup.offsetWidth;
    const popupHeight = popup.offsetHeight;
    const MARGIN = 8;
    const GAP = 8; // matches the CSS 8px gap between popup and citation

    // Bounds come from the chat content container when available, so the
    // popup respects the sidebar / header edges rather than the raw window.
    const container = findBoundingContainer(popup);
    const containerRect = container
      ? container.getBoundingClientRect()
      : { left: 0, right: window.innerWidth, top: 0, bottom: window.innerHeight };
    const leftBound   = containerRect.left   + MARGIN;
    const rightBound  = containerRect.right  - MARGIN;
    const topBound    = containerRect.top    + MARGIN;
    const bottomBound = containerRect.bottom - MARGIN;

    // ── Vertical: prefer 'above' but flip when there isn't room ─────
    const spaceAbove = wrapperRect.top - topBound;
    const spaceBelow = bottomBound - wrapperRect.bottom;
    const neededVertical = popupHeight + GAP;
    let nextDirection = 'above';
    if (neededVertical > spaceAbove && spaceBelow > spaceAbove) {
      nextDirection = 'below';
    }

    // ── Horizontal: center on wrapper, shift inward if clipped ──────
    // hOffset is added to the default centered position, so the popup
    // center sits at wrapperCenterX + hOffset.  Bound hOffset so the
    // arrow doesn't leave the popup body.
    const wrapperCenterX = wrapperRect.left + wrapperRect.width / 2;
    const desiredLeft  = wrapperCenterX - popupWidth / 2;
    const desiredRight = desiredLeft + popupWidth;
    let nextOffset = 0;
    if (desiredLeft < leftBound) {
      nextOffset = leftBound - desiredLeft;
    } else if (desiredRight > rightBound) {
      nextOffset = rightBound - desiredRight;
    }
    // Keep the arrow inside the popup (leave ~12 px room from either edge)
    const ARROW_INSET = 12;
    const maxOffset = (popupWidth / 2) - ARROW_INSET;
    if (nextOffset > maxOffset)  nextOffset = maxOffset;
    if (nextOffset < -maxOffset) nextOffset = -maxOffset;

    if (nextDirection !== direction) setDirection(nextDirection);
    if (nextOffset !== hOffset) setHOffset(nextOffset);
  }, [refData, direction, hOffset]);

  if (!refData) return null;

  const title = (refData.title && refData.title.trim()) || refData.source || '';
  const typeLabel = (refData.type && refData.type.trim()) || (refData.filetype && refData.filetype.trim()) || '';
  const isExternalRefPlaceholder = typeLabel.toLowerCase() === 'external reference';
  const displayType = (typeLabel && !isExternalRefPlaceholder) ? typeLabel : '';

  // Canadian / Non-Canadian — only render when the backend set it.
  let guidelineLabel = '';
  if (refData.canadian_guideline != null && String(refData.canadian_guideline).trim() !== '') {
    guidelineLabel = String(refData.canadian_guideline).toLowerCase() === 'true'
      ? tFn('canadian')
      : tFn('non_canadian');
  }

  const pageLabel = refData.page != null && String(refData.page).trim() !== '' ? String(refData.page) : '';

  // Strength: derived from the user-facing Type label only. We deliberately
  // pass `displayType` (not `typeLabel`) so the legacy "External Reference"
  // placeholder doesn't get classified — strength should be blank when the
  // reference wasn't actually tagged with one of the eight categories.
  const strengthKey = STRENGTH_KEY_BY_TYPE(displayType);
  const strengthText = strengthLabel(strengthKey, getLang());

  // Title is the clickable affordance now (no separate "Open source" button).
  // Three states:
  //   - clickable + URL ready    → render as <a> opening in a new tab
  //   - clickable but URL loading → render as <span> with subtle loading hint
  //   - not clickable (AI Source w/o URL) → render as plain <span>
  const titleClickable = refData.clickable !== false && !!url && !loadingUrl;
  const titleNode = titleClickable ? (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="tooltip-title-link"
      title={tFn(refData.is_web_result ? 'click_open_website' : 'click_open_file')}
      onClick={(e) => e.stopPropagation()}
    >
      {title}
    </a>
  ) : (
    <span>{title}</span>
  );

  // Style overrides applied per measurement:
  //   - hOffset shifts the popup body left/right via the transform
  //   - --arrow-x repositions the ::after arrow so it still points at the
  //     citation when the popup has been nudged inward
  const popupStyle = {
    transform: `translateX(calc(-50% + ${hOffset}px))`,
    '--arrow-x': `calc(50% - ${hOffset}px)`,
  };

  return (
    <div
      ref={popupRef}
      className={`citation-tooltip-popup tooltip-${direction}`}
      role="tooltip"
      style={popupStyle}
    >
      <div className="tooltip-title">
        {titleNode}
        {loadingUrl && (
          <span className="tooltip-title-loading"> · {tFn('loading_url')}</span>
        )}
      </div>
      <div className="tooltip-meta-grid">
        {displayType && (
          <>
            <span className="tooltip-meta-label">{tFn('type_label')}</span>
            <span className="tooltip-meta-value">{localizeType(displayType, getLang())}</span>
          </>
        )}
        {guidelineLabel && (
          <>
            <span className="tooltip-meta-label">{tFn('guideline_label')}</span>
            <span className="tooltip-meta-value">{guidelineLabel}</span>
          </>
        )}
        {pageLabel && (
          <>
            <span className="tooltip-meta-label">{tFn('page_label')}</span>
            <span className="tooltip-meta-value">{pageLabel}</span>
          </>
        )}
        {strengthText && (
          <>
            <span className="tooltip-meta-label">{tFn('strength_label')}</span>
            <span className={`tooltip-meta-value tooltip-strength tooltip-strength-${strengthKey}`}>{strengthText}</span>
          </>
        )}
        {(refData.metadata_tag?.trim() || refData.clickable === false) && (
          <>
            <span className="tooltip-meta-label">{tFn('source_label')}</span>
            <span className="tooltip-meta-value tooltip-ai-source">
              {refData.metadata_tag?.trim()
                ? localizeMetaTag(refData.metadata_tag.trim(), getLang())
                : tFn('ai_source')}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

// ── Feature 6: Citation link with hover tooltip ────────────────────────────────
const CitationLink = ({ num, refId, refData, onGetSignedUrl, tFn }) => {
  const [hovered, setHovered] = useState(false);

  const handleClick = (e) => {
    e.preventDefault();
    const el = document.getElementById(refId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('highlight-reference');
      setTimeout(() => el.classList.remove('highlight-reference'), 2000);
    }
  };

  return (
    <span
      className="citation-wrapper"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <a href={`#${refId}`} className="citation-link" onClick={handleClick}>
        {num}
      </a>
      {hovered && (
        <CitationTooltip refData={refData} onGetSignedUrl={onGetSignedUrl} tFn={tFn} />
      )}
    </span>
  );
};

// ── Citation text renderer (inline [1] [2] → interactive links) ────────────────
const CitationText = ({ children, messageId, references, onGetSignedUrl, tFn, searchQuery }) => {
  if (typeof children === 'string') {
    const parts = children.split(/(\[\s*[\d,\s]+\s*\])/g);
    return (
      <>
        {parts.map((part, index) => {
          const match = part.match(/^\[(\s*[\d,\s]+\s*)\]$/);
          if (match) {
            const nums = match[1].split(',').map(n => n.trim()).filter(n => /^\d+$/.test(n));
            if (nums.length === 0) return part;
            return (
              <span key={index} className="citation-group">
                {'['}
                {nums.map((num, i) => {
                  const refId = messageId ? `ref-${messageId}-${num}` : `ref-${num}`;
                  const refData = references ? references.find(r => String(r.id) === num) : null;
                  return (
                    <React.Fragment key={i}>
                      {i > 0 && ', '}
                      <CitationLink
                        num={num}
                        refId={refId}
                        refData={refData}
                        onGetSignedUrl={onGetSignedUrl}
                        tFn={tFn}
                      />
                    </React.Fragment>
                  );
                })}
                {']'}
              </span>
            );
          }
          // Non-citation plain text — apply search highlight if a query is active.
          const highlighted = highlightText(part, searchQuery);
          return Array.isArray(highlighted)
            ? <React.Fragment key={index}>{highlighted}</React.Fragment>
            : highlighted;
        })}
      </>
    );
  }

  if (Array.isArray(children)) {
    return (
      <>
        {children.map((child, index) => (
          <CitationText key={index} messageId={messageId} references={references} onGetSignedUrl={onGetSignedUrl} tFn={tFn} searchQuery={searchQuery}>
            {child}
          </CitationText>
        ))}
      </>
    );
  }

  return children;
};

// Product name shown in the chat header. Single source of truth so every
// export channel (.txt, PDF) carries the exact same branding as the app —
// it is a brand name, so it is not translated.
const APP_TITLE = 'THCC PRISM';

// ── Timestamp parsing ─────────────────────────────────────────────────────────
// The backend stores created_at/updated_at as naive UTC (datetime.utcnow) and
// Pydantic serializes them with NO timezone designator, e.g. "2026-06-29T14:30:00".
// Per the ECMAScript spec, `new Date()` parses a timezone-less datetime string as
// LOCAL time, so those server values land hours off from the true (UTC) instant —
// the header clock then jumps to the wrong time once a freshly-asked question
// (created locally with toISOString()'s trailing "Z") is replaced by the reloaded
// server copy. Normalize by appending "Z" when no offset/zone is present so the
// string is always interpreted as UTC.
function parseServerDate(value) {
  if (value == null) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value.trim());
    return new Date(hasZone ? value : `${value}Z`);
  }
  return new Date(value);
}

// ── Feature 1 / change 10: Group conversations by time period ─────────────────
// Hierarchy: Today → Yesterday → Last 7 Days → then by Month, Year (e.g.
// "July, 2026"). Month/year buckets are flagged collapsible (`collapsible:true`)
// so the sidebar renderer can show a disclosure arrow and default them to
// collapsed.
const MONTHS_EN = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const MONTHS_FR = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];

function groupConversationsByDate(convs, labels, lang = 'en') {
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = startOfDay(now);
  const yesterday = new Date(today - 86400000);
  const lastWeek  = new Date(today - 6 * 86400000);
  const monthNames = lang === 'fr' ? MONTHS_FR : MONTHS_EN;
  const formatMonthYear = (date) => {
    const m = monthNames[date.getMonth()];
    const label = lang === 'fr'
      ? `${m.charAt(0).toUpperCase()}${m.slice(1)} ${date.getFullYear()}`
      : `${m}, ${date.getFullYear()}`;
    return label;
  };

  const todayBucket     = { key: 'today',     label: labels.today,      items: [], collapsible: false };
  const yesterdayBucket = { key: 'yesterday', label: labels.yesterday,  items: [], collapsible: false };
  const last7Bucket     = { key: 'last7',     label: labels.last_7_days,items: [], collapsible: false };
  const monthBuckets    = new Map(); // key = "YYYY-MM" → {key,label,items,collapsible:true,sortKey}

  convs.forEach(conv => {
    const raw = parseServerDate(conv.updated_at || conv.created_at);
    const d = startOfDay(raw);
    if (d >= today)            todayBucket.items.push(conv);
    else if (d >= yesterday)   yesterdayBucket.items.push(conv);
    else if (d >= lastWeek)    last7Bucket.items.push(conv);
    else {
      const monthKey = `${raw.getFullYear()}-${String(raw.getMonth()).padStart(2, '0')}`;
      let bucket = monthBuckets.get(monthKey);
      if (!bucket) {
        bucket = {
          key: `month-${monthKey}`,
          label: formatMonthYear(raw),
          items: [],
          collapsible: true,
          sortKey: raw.getFullYear() * 12 + raw.getMonth(),
        };
        monthBuckets.set(monthKey, bucket);
      }
      bucket.items.push(conv);
    }
  });

  // Sort month buckets newest first
  const sortedMonths = Array.from(monthBuckets.values()).sort((a, b) => b.sortKey - a.sortKey);

  return [todayBucket, yesterdayBucket, last7Bucket, ...sortedMonths].filter(g => g.items.length > 0);
}

// ─────────────────────────────────────────────────────────────────────────────
function Chat() {
  const [user, setUser]                       = useState(null);
  const [conversations, setConversations]     = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [currentConversation, setCurrentConversation]     = useState(null);
  const [messages, setMessages]               = useState([]);
  const [inputMessage, setInputMessage]       = useState('');
  const [loading, setLoading]                 = useState(false);
  const [searchQuery, setSearchQuery]         = useState('');
  const [isNewChat, setIsNewChat]             = useState(false);
  const [conversationStates, setConversationStates] = useState({});
  const [activeOperations, setActiveOperations]     = useState(new Set());
  const [messageReferences, setMessageReferences]   = useState({});
  const [piiError, setPiiError]               = useState('');
  const [uploadedFile, setUploadedFile]       = useState(null);
  const [uploadError, setUploadError]         = useState('');
  const [virusScanError, setVirusScanError]   = useState('');
  const [feedbackGiven, setFeedbackGiven]     = useState({});
  const [showFeedbackModal, setShowFeedbackModal] = useState(null);
  const [feedbackComment, setFeedbackComment] = useState('');

  // Feature 5: per-message reference collapse state
  const [collapsedRefs, setCollapsedRefs] = useState({});

  // Feature 7: copy feedback per message
  const [copiedMsg, setCopiedMsg] = useState(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  // Response mode selector — persisted in localStorage
  const [responseMode, setResponseMode] = useState(
    () => localStorage.getItem('prism_response_mode') || 'detailed'
  );
  const [showResponseMenu, setShowResponseMenu] = useState(false);
  const responseModeRef = useRef(null);

  // Feature 8: language (browser-detect, localStorage-persist)
  const [language, setLanguage] = useState(getLang);

  // In-app "Help" feedback modal — opens from the Chat header.
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Restore-draft prompt: { id, text } when a saved draft is offered for the
  // active conversation. Cleared once the user clicks Restore or Discard.
  const [pendingDraft, setPendingDraft] = useState(null);

  // Sidebar collapse + collapsed-mode popovers
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('prism_sidebar_collapsed') === '1'
  );
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [collapsedSearchOpen, setCollapsedSearchOpen] = useState(false);
  const [collapsedChatsOpen, setCollapsedChatsOpen] = useState(false);

  // Phone-only off-canvas navigation drawer. On narrow screens the sidebar is
  // hidden by default and slides in over the chat as an overlay; on tablet/
  // desktop this flag is inert (CSS keeps the sidebar in the normal flow).
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Open the drawer in its full (expanded) form regardless of the persisted
  // desktop-collapse state — the icon rail makes no sense as a phone drawer.
  const openMobileNav = () => { setSidebarCollapsed(false); setMobileNavOpen(true); };
  const closeMobileNav = () => setMobileNavOpen(false);

  // Change #10: which month/year groups are expanded in the sidebar.
  // Default = collapsed; entry present and true means user opened it.
  const [expandedMonths, setExpandedMonths] = useState({});

  const messagesEndRef       = useRef(null);
  const streamingIntervalsRef = useRef(new Map());
  const messagesContainerRef = useRef(null);  // Feature 2
  const isAtBottomRef        = useRef(true);  // Feature 2
  const textareaRef          = useRef(null);  // Feature 3
  const signedUrlCacheRef    = useRef({});    // Feature 6: cache signed URLs
  const uploadedFileRef      = useRef(null);
  const downloadMenuRef      = useRef(null);
  const fileUploadRef        = useRef(null);  // trigger file picker from attach button
  const userMenuRef          = useRef(null);
  const collapsedSearchRef   = useRef(null);
  const collapsedChatsRef    = useRef(null);
  // Tracks which conversation contexts we've already evaluated for a saved
  // draft this mount, so chat switches don't re-prompt after the user has
  // already responded (or after the draft cleared during send).
  const draftCheckedRef      = useRef(new Set());
  // One-shot guard so we only auto-route the user back to their last draft
  // context on the very first conversations load (not on every subsequent
  // loadConversations refresh — e.g. after sending a message).
  const initialDraftRoutedRef = useRef(false);
  const navigate             = useNavigate();

  // Feature 8: translation helper
  const t = useCallback((key) => TRANSLATIONS[language][key] || key, [language]);

  // Conversation titles are user/LLM text and pass through untouched — except
  // the backend's "generating" placeholder, which is UI copy and must follow
  // the selected language.
  const displayTitle = useCallback(
    (title) => (title === TITLE_PLACEHOLDER ? t('generating_title') : title),
    [t],
  );

  // Feature 6: signed URL with local cache (prevents redundant API calls per hover)
  const getSignedUrlForTooltip = useCallback(async (filename) => {
    if (signedUrlCacheRef.current[filename]) return signedUrlCacheRef.current[filename];
    try {
      const resp = await conversationsAPI.getSignedUrl(filename);
      if (resp.signed_url) {
        signedUrlCacheRef.current[filename] = resp.signed_url;
        return resp.signed_url;
      }
    } catch (_) {}
    return null;
  }, []);

  useEffect(() => {
    loadUser();
    loadConversations();
    return () => { streamingIntervalsRef.current.forEach(c => c?.abort?.()); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Feature 8: persist language choice
  useEffect(() => {
    localStorage.setItem('prism_language', language);
  }, [language]);

  // Persist response mode so it survives page reloads
  useEffect(() => {
    localStorage.setItem('prism_response_mode', responseMode);
  }, [responseMode]);

  // Collapse references by default: whenever new message IDs appear in messageReferences,
  // explicitly set them to true (collapsed) if they haven't been toggled yet.
  useEffect(() => {
    setCollapsedRefs(prev => {
      let changed = false;
      const next = { ...prev };
      Object.keys(messageReferences).forEach(id => {
        if (!(id in next)) {
          next[id] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [messageReferences]);

  // Feature 3: reset textarea height when input is cleared
  useEffect(() => {
    if (!inputMessage && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [inputMessage]);

  // Autosave the query box content per-conversation. Writes on every change
  // (including programmatic restores after stream errors), removes the key
  // when the input is empty (e.g. right after a successful send). Survives
  // any non-manual logout — see clearAllDrafts() call in handleLogout.
  //
  // The prev-input check makes sure we only persist when the user actually
  // edited the text. Without it, switching conversations while the textarea
  // still holds the previous chat's typed text would clobber the new chat's
  // saved draft with the carried-over content.
  const prevDraftInputRef = useRef('');
  useEffect(() => {
    if (inputMessage === prevDraftInputRef.current) return;
    prevDraftInputRef.current = inputMessage;
    const id = currentConversation?.id || 'new-chat';
    setDraft(id, inputMessage);
  }, [inputMessage, currentConversation?.id]);

  // When the active conversation context changes, check once whether a
  // non-empty draft is saved for it. If so AND the textarea is empty,
  // surface the Restore / Discard banner. We only prompt once per (mount,
  // conversation) pair so the user isn't re-asked after acting.
  useEffect(() => {
    if (!currentConversation && !isNewChat) return;
    const id = currentConversation?.id || 'new-chat';
    if (draftCheckedRef.current.has(id)) return;
    draftCheckedRef.current.add(id);
    const saved = getDraft(id);
    if (saved && saved.trim() && !inputMessage.trim()) {
      setPendingDraft({ id, text: saved });
    }
  }, [currentConversation?.id, isNewChat]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close response mode dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (responseModeRef.current && !responseModeRef.current.contains(e.target)) {
        setShowResponseMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Persist sidebar collapse state
  useEffect(() => {
    localStorage.setItem('prism_sidebar_collapsed', sidebarCollapsed ? '1' : '0');
    if (sidebarCollapsed) {
      setUserMenuOpen(false);
    } else {
      setCollapsedSearchOpen(false);
      setCollapsedChatsOpen(false);
    }
  }, [sidebarCollapsed]);

  // Close user menu / collapsed popovers on outside click
  useEffect(() => {
    const handler = (e) => {
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
      if (collapsedSearchOpen && collapsedSearchRef.current && !collapsedSearchRef.current.contains(e.target)) {
        setCollapsedSearchOpen(false);
      }
      if (collapsedChatsOpen && collapsedChatsRef.current && !collapsedChatsRef.current.contains(e.target)) {
        setCollapsedChatsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [userMenuOpen, collapsedSearchOpen, collapsedChatsOpen]);

  const getConversationState = (conversationId = null) => {
    const id = conversationId || currentConversation?.id || 'new-chat';
    return conversationStates[id] || {
      messages: [], streamingMessage: '', isStreaming: false, references: {}, sending: false
    };
  };

  const updateConversationState = (conversationId = null, updates) => {
    const id = conversationId || currentConversation?.id || 'new-chat';
    setConversationStates(prev => ({ ...prev, [id]: { ...prev[id], ...updates } }));
  };

  const getCurrentMessages       = () => getConversationState().messages;
  const getCurrentStreamingMessage = () => getConversationState().streamingMessage;
  const getCurrentIsStreaming    = () => getConversationState().isStreaming;
  const getCurrentSending        = () => getConversationState().sending;

  // ── Feature 2: smart scroll — only follow bottom if user is already there ──
  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  const scrollToBottom = useCallback(() => {
    if (isAtBottomRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    if (searchQuery.trim()) handleSearch(searchQuery);
    else setFilteredConversations(conversations);
  }, [searchQuery, conversations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showDownloadMenu) return;
    const close = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target))
        setShowDownloadMenu(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showDownloadMenu]);

  // Keep uploadedFile ref in sync with state (avoids stale closure in cleanup)
  useEffect(() => { uploadedFileRef.current = uploadedFile; }, [uploadedFile]);

  const loadUser = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
      // endSession() rather than navigate(): a router hop would leave this
      // component's state mounted behind the login screen. A full replace
      // guarantees the sign-in page that appears is a clean one.
      endSession();
    }
  };

  const loadConversations = async () => {
    try {
      setLoading(true);
      const data = await conversationsAPI.list();
      setConversations(data);
      setFilteredConversations(data);

      // First load after mount — if the user had an unsent draft when they
      // were last here, drop them back into that context so the restore
      // banner is immediately visible. Without this they'd land on the empty
      // no-conversation state and the banner would only appear after they
      // manually opened a chat.
      if (!initialDraftRoutedRef.current) {
        initialDraftRoutedRef.current = true;
        const lastId = getLastDraftId();
        if (lastId === 'new-chat') {
          setIsNewChat(true);
        } else if (lastId && data.find(c => c.id === lastId)) {
          await loadConversation(lastId);
        }
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Destroy the Turbopuffer namespace ONLY if it's an unbound in-flight upload
  // (one the user attached but never sent). Once a namespace is bound to a
  // saved conversation (isPersisted=true), it lives with the conversation —
  // switching away or starting a new chat must NOT delete it. Cleanup of a
  // persisted namespace happens via DELETE /conversations/{id}/attachment
  // (user-removed) or DELETE /conversations/{id} (whole-conversation delete).
  const dropUnboundUpload = async () => {
    const target = uploadedFileRef.current;
    if (target?.namespace && target.isPersisted === false) {
      try { await fileAPI.cleanup(target.namespace); }
      catch (err) { console.warn('Namespace cleanup failed (non-fatal):', err); }
    }
    setUploadedFile(null);
    setUploadError('');
  };

  // Remove the attachment from the CURRENT conversation. Persisted attachments
  // are removed via the detach endpoint (which clears the conv row + destroys
  // the namespace server-side); unbound uploads are destroyed via /cleanup.
  const clearUploadedFile = async () => {
    const target = uploadedFileRef.current;
    if (target?.namespace) {
      try {
        if (target.isPersisted && currentConversation?.id) {
          await conversationsAPI.detachAttachment(currentConversation.id);
          setCurrentConversation(prev =>
            prev ? { ...prev, temp_namespace: null, attachment_filename: null } : prev
          );
        } else {
          await fileAPI.cleanup(target.namespace);
        }
      } catch (err) { console.warn('Attachment removal failed (non-fatal):', err); }
    }
    setUploadedFile(null);
    setUploadError('');
  };

  const loadConversation = async (conversationId) => {
    // On phones the sidebar is an overlay — collapse it once a chat is picked
    // so the conversation is visible.
    setMobileNavOpen(false);
    // Drop any in-flight (unsent) upload; keep persisted namespaces alive on
    // their owning conversations.
    await dropUnboundUpload();
    try {
      const existingState = getConversationState(conversationId);
      if (existingState.isStreaming || existingState.sending) {
        setCurrentConversation({ id: conversationId });
        setMessages(existingState.messages);
        setIsNewChat(false);
        return;
      }
      setLoading(true);
      const data = await conversationsAPI.get(conversationId);
      setCurrentConversation(data);
      setMessages(data.messages);
      setIsNewChat(false);
      updateConversationState(conversationId, {
        messages: data.messages, isStreaming: false, sending: false, streamingMessage: ''
      });
      setMessageReferences(
        data.references && Object.keys(data.references).length > 0 ? data.references : {}
      );
      // Rehydrate the attached-file UI from the conversation row. Persisted
      // attachments survive chat switching — see dropUnboundUpload.
      if (data.temp_namespace) {
        setUploadedFile({
          namespace: data.temp_namespace,
          name: data.attachment_filename || 'Attached Document',
          status: 'completed',
          isPersisted: true,
        });
      } else {
        setUploadedFile(null);
      }
      cleanupStreamingStates();
      // Scroll to bottom when switching to a conversation
      isAtBottomRef.current = true;
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const cleanupStreamingStates = () => {
    const currentId = currentConversation?.id || 'new-chat';
    const validIds = new Set([currentId, 'new-chat']);
    conversations.forEach(conv => validIds.add(conv.id));
    setConversationStates(prev => {
      const cleaned = {};
      Object.keys(prev).forEach(id => { if (validIds.has(id)) cleaned[id] = prev[id]; });
      return cleaned;
    });
  };

  const handleFeedbackClick = async (messageId, rating) => {
    const current = feedbackGiven[messageId];
    if (current === rating) {
      setFeedbackGiven(prev => { const n = { ...prev }; delete n[messageId]; return n; });
      return;
    }
    if (rating === 'negative') {
      setShowFeedbackModal(messageId);
      setFeedbackComment('');
      return;
    }
    await submitFeedback(messageId, rating, '');
  };

  const submitFeedback = async (messageId, rating, comment = '') => {
    try {
      await feedbackAPI.create({
        message_id: messageId,
        conversation_id: currentConversation.id,
        rating,
        comment,
      });
      setFeedbackGiven(prev => ({ ...prev, [messageId]: rating }));
      setShowFeedbackModal(null);
      setFeedbackComment('');
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      alert(t('feedback_error'));
    }
  };

  const handleFeedbackModalSubmit = async () => {
    if (!showFeedbackModal) return;
    await submitFeedback(showFeedbackModal, 'negative', feedbackComment);
  };

  const closeFeedbackModal = () => {
    setShowFeedbackModal(null);
    setFeedbackComment('');
  };

  const createNewConversation = () => {
    setMobileNavOpen(false);
    setCurrentConversation(null);
    setMessages([]);
    setIsNewChat(true);
    setConversationStates(prev => { const c = { ...prev }; delete c['new-chat']; return c; });
    setInputMessage('');
    setPiiError('');
    setMessageReferences({});
    setFeedbackGiven({});
    // Don't destroy persisted namespaces — they stay with their conversations.
    // Only drop an in-flight (never-sent) upload so we don't leak it.
    dropUnboundUpload();
  };

  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    if (!window.confirm(t('delete_confirm'))) return;
    try {
      // Abort any active stream for this conversation before deleting
      const ctrl = streamingIntervalsRef.current.get(conversationId);
      if (ctrl) { ctrl.abort(); streamingIntervalsRef.current.delete(conversationId); }

      await conversationsAPI.delete(conversationId);

      setConversations(prev => prev.filter(c => c.id !== conversationId));
      setFilteredConversations(prev => prev.filter(c => c.id !== conversationId));
      setConversationStates(prev => {
        const s = { ...prev };
        delete s[conversationId];
        // Reset 'new-chat' temp state so the sending/streaming indicator doesn't bleed through
        s['new-chat'] = { messages: [], streamingMessage: '', isStreaming: false, references: {}, sending: false };
        return s;
      });

      // If the deleted conversation was open, reset to new-chat state.
      // The backend's DELETE /conversations/{id} already destroyed the
      // attached namespace (if any), so we just clear local state — no
      // extra /cleanup call is needed.
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
        setIsNewChat(true);
        setInputMessage('');
        setPiiError('');
        setMessageReferences({});
        setFeedbackGiven({});
        setUploadedFile(null);
        setUploadError('');
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const handleSearch = async (query) => {
    if (!query.trim()) { setFilteredConversations(conversations); return; }
    try {
      const results = await conversationsAPI.search(query);
      setFilteredConversations(results);
    } catch (error) {
      console.error('Failed to search:', error);
      setFilteredConversations(
        conversations.filter(c => c.title.toLowerCase().includes(query.toLowerCase()))
      );
    }
  };

  // ── Feature 5: toggle reference section collapse ───────────────────────────
  // Default is collapsed (undefined → collapsed). Track explicit user choice as false=open / true=closed.
  const toggleRefs = (messageId) => {
    setCollapsedRefs(prev => ({
      ...prev,
      [messageId]: prev[messageId] === false ? true : false,
    }));
  };

  // Returns the label/value pairs the references list shows in the UI, in
  // the same order, so every export channel (clipboard HTML, .txt, PDF)
  // renders the same set. Empty/blank fields are omitted; nothing is escaped
  // here — the caller is responsible for escaping when emitting HTML.
  const refMetaPairs = (r) => {
    const pairs = [];
    const typeRaw = (r.type?.trim()) || (r.filetype?.trim()) || '';
    const typeDisplay = typeRaw.toLowerCase() === 'external reference' ? '' : typeRaw;
    if (typeDisplay) pairs.push([t('type_label'), localizeType(typeDisplay, language)]);

    if (Array.isArray(r.pages) && r.pages.length > 0) {
      pairs.push([r.pages.length > 1 ? t('pages_label') : t('page_label'), r.pages.join(', ')]);
    } else if (r.page != null && String(r.page).trim() !== '') {
      pairs.push([t('page_label'), String(r.page)]);
    }

    if (r.canadian_guideline != null && String(r.canadian_guideline).trim() !== '') {
      pairs.push([t('guideline_label'),
        String(r.canadian_guideline).toLowerCase() === 'true' ? t('canadian') : t('non_canadian')]);
    }

    if (r.impact != null && String(r.impact).toLowerCase() === 'true') {
      pairs.push([t('journal_label'), t('reputable_journal')]);
    }

    const sKey = STRENGTH_KEY_BY_TYPE(typeDisplay);
    if (sKey) pairs.push([t('strength_label'), strengthLabel(sKey, language)]);

    // Source / AI Source — mirrors the conditions in renderReferences.
    const showSource = (r.metadata_tag?.trim())
      || (r.clickable !== false && r.is_web_result && (r.filetype === 'External Reference' || r.filetype === ''))
      || r.clickable === false;
    if (showSource) pairs.push([t('source_label'), r.metadata_tag?.trim() ? localizeMetaTag(r.metadata_tag.trim(), language) : t('ai_source')]);

    return pairs;
  };

  // Header date (change #9): the moment of the LAST question asked. This one
  // tracks the live conversation, so it moves to today as soon as a follow-up
  // is asked — which is why the exports below use getChatDateRangeLabel()
  // instead. Returns null when the conversation has no usable timestamp.
  const getChatDateLabel = () => {
    const msgs = getCurrentMessages();
    let lastDate = null;
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'user' && msgs[i].created_at) {
        lastDate = msgs[i].created_at;
        break;
      }
    }
    if (!lastDate && msgs.length > 0) lastDate = msgs[msgs.length - 1].created_at;
    if (!lastDate && currentConversation?.updated_at) lastDate = currentConversation.updated_at;
    if (!lastDate) return null;
    const dt = parseServerDate(lastDate);
    if (!dt || Number.isNaN(dt.getTime())) return null;
    return dt.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  // Date stamped on an exported transcript: WHEN THE CHAT HAPPENED, never when
  // it was downloaded. Anchored on the FIRST message so re-opening an old chat
  // (or asking a follow-up today) can't drag the label forward to today. A chat
  // that spans more than one day is stamped as a range, e.g.
  // "August 6, 2026 – August 10, 2026". Returns null if no timestamp exists.
  const getChatDateRangeLabel = () => {
    const stamps = getCurrentMessages()
      .map(m => m.created_at)
      .filter(Boolean)
      .map(parseServerDate)
      .filter(d => d && !Number.isNaN(d.getTime()))
      .sort((a, b) => a - b);

    // Fall back to the conversation row when messages carry no timestamps —
    // created_at first, since updated_at is bumped by every new message.
    if (stamps.length === 0) {
      const fallback = parseServerDate(
        currentConversation?.created_at || currentConversation?.updated_at || null
      );
      if (!fallback || Number.isNaN(fallback.getTime())) return null;
      stamps.push(fallback);
    }

    const locale = language === 'fr' ? 'fr-CA' : 'en-CA';
    const fmt = (d) => d.toLocaleDateString(locale, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
    const first = stamps[0];
    const last = stamps[stamps.length - 1];
    const sameDay = first.toDateString() === last.toDateString();
    return sameDay ? fmt(first) : `${fmt(first)} – ${fmt(last)}`;
  };

  // ── Feature 7: copy message as rich text (HTML for Word/Docs, plain fallback)
  const handleCopyMessage = async (msg) => {
    const refs = messageReferences[msg.id];

    // Build references HTML
    let refsHtml = '';
    if (refs && refs.length > 0) {
      refsHtml = `<hr style="margin:12px 0"><h4 style="margin:0 0 6px">${t('references')}</h4><ol style="margin:0;padding-left:20px">`;
      refs.forEach(r => {
        refsHtml += `<li><strong>[${r.id}]</strong> ${r.source}`;
        const metaSuffix = refMetaPairs(r).map(([k, v]) => ` — ${k} ${v}`).join('');
        refsHtml += metaSuffix;
        refsHtml += '</li>';
      });
      refsHtml += '</ol>';
    }

    // Grab already-rendered HTML from the DOM — avoids any markdown parser dependency.
    // All HTML pulled from the DOM is sanitized before being re-emitted.
    const msgEl = document.querySelector(`[data-msg-id="${msg.id}"]`);
    const rawBody = (msgEl ? msgEl.innerHTML : `<p>${msg.content}</p>`) + refsHtml;
    const bodyHtml = sanitizeHtml(rawBody);

    const fullHtml = `<html><head><meta charset="utf-8"></head><body>
      <style>
        table{border-collapse:collapse;width:100%}
        th,td{border:1px solid #999;padding:6px 10px;text-align:left}
        th{background:#f0f0f0;font-weight:bold}
      </style>
      ${bodyHtml}
    </body></html>`;

    // Plain text: use the sanitized fragment.
    const tmpDiv = document.createElement('div');
    tmpDiv.innerHTML = bodyHtml;
    const plainText = tmpDiv.innerText;

    try {
      const htmlBlob = new Blob([fullHtml], { type: 'text/html' });
      const textBlob = new Blob([plainText], { type: 'text/plain' });
      await navigator.clipboard.write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })]);
    } catch (_) {
      // Fallback for browsers without ClipboardItem support
      try { await navigator.clipboard.writeText(plainText); } catch (_) {}
    }

    setCopiedMsg(msg.id);
    setTimeout(() => setCopiedMsg(null), 2000);
  };

  // Feature 7: download conversation as .txt (clean plain text, no markdown syntax)
  const handleDownloadTxt = () => {
    setShowDownloadMenu(false);
    const msgs = getCurrentMessages();
    const title = currentConversation?.title || 'PRISM Chat';
    // Mirror the app header: product name, conversation title, then the date
    // the chat took place — never the date of the download.
    const chatDate = getChatDateRangeLabel();
    let text = `${APP_TITLE}\n`;
    text += `${title}\n`;
    if (chatDate) text += `${chatDate}\n`;
    text += '='.repeat(60) + '\n\n';
    msgs.forEach(msg => {
      if (msg.role === 'user') {
        text += 'You:\n' + msg.content + '\n';
      } else {
        text += 'PRISM:\n';
        // Use rendered DOM text to strip markdown syntax
        const msgEl = document.querySelector(`[data-msg-id="${msg.id}"]`);
        text += msgEl ? msgEl.innerText : msg.content;
        const refs = messageReferences[msg.id];
        if (refs && refs.length > 0) {
          text += '\n\nReferences:\n';
          refs.forEach(r => {
            const metaSuffix = refMetaPairs(r).map(([k, v]) => ` — ${k} ${v}`).join('');
            text += `[${r.id}] ${r.source}${metaSuffix}\n`;
          });
        }
      }
      text += '\n' + '-'.repeat(40) + '\n\n';
    });
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/[^a-z0-9]/gi, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Feature 7: download conversation as PDF.
  // Builds the message body HTML on the client (same content as the old
  // print preview) and POSTs it to the backend, which sanitizes it again
  // and renders to a real PDF via WeasyPrint. No print dialog, no second
  // window — the browser saves the file directly.
  const handleDownloadPdf = async () => {
    setShowDownloadMenu(false);
    const msgs = getCurrentMessages();
    const title = currentConversation?.title || 'PRISM Chat';

    const escapeRef = (val) => String(val || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    let bodyHtml = '';
    msgs.forEach(msg => {
      if (msg.role === 'user') {
        const safeContent = msg.content
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>');
        bodyHtml += `<div class="msg user">
          <div class="avatar">${language === 'fr' ? 'Vous' : 'You'}</div>
          <div class="content">${safeContent}</div>
        </div>`;
      } else {
        const msgEl = document.querySelector(`[data-msg-id="${msg.id}"]`);
        const assistantHtmlRaw = msgEl ? msgEl.innerHTML : `<p>${msg.content}</p>`;
        const assistantHtml = sanitizeHtml(assistantHtmlRaw);
        const refs = messageReferences[msg.id];
        let refsHtml = '';
        if (refs && refs.length > 0) {
          refsHtml = `<div class="refs"><h4>${t('references')}</h4><ol>`;
          refs.forEach(r => {
            refsHtml += `<li><strong>[${escapeRef(r.id)}]</strong> ${escapeRef(r.source)}`;
            refsHtml += refMetaPairs(r)
              .map(([k, v]) => ` — ${escapeRef(k)} ${escapeRef(v)}`)
              .join('');
            refsHtml += '</li>';
          });
          refsHtml += '</ol></div>';
        }
        bodyHtml += `<div class="msg assistant">
          <div class="avatar">PRISM</div>
          <div class="content">${assistantHtml}${refsHtml}</div>
        </div>`;
      }
    });

    try {
      await conversationExportAPI.toPdf({
        title,
        body_html: bodyHtml,
        // Date the chat took place, not the date of the export, and the same
        // header/banner strings the app shows — so the PDF banner is a copy of
        // the in-app one instead of a separate hard-coded version.
        date_label: getChatDateRangeLabel() || '',
        app_title: APP_TITLE,
        banners: [t('phi_warning'), t('disclaimer')],
      });
    } catch (err) {
      console.error('PDF export failed', err);
      alert(err?.response?.data?.detail || 'Could not download PDF. Please try again.');
    }
  };

  const handleReferenceClick = async (ref) => {
    if (ref.clickable === false) return;
    try {
      if (ref.is_web_result && ref.url) { window.open(ref.url, '_blank'); return; }
      const filename = typeof ref === 'string' ? ref : ref.source;
      const response = await conversationsAPI.getSignedUrl(filename);
      if (response.signed_url) {
        window.open(response.signed_url, '_blank');
      } else {
        alert('Could not generate signed URL for file');
      }
    } catch (error) {
      console.error('Error opening reference:', error);
      const filename = typeof ref === 'string' ? ref : ref.source;
      alert(error.response?.status === 404 ? `${t('file_not_found')} ${filename}` : `${t('error_opening')} ${filename}`);
    }
  };

  const handleFileUploadSuccess = (result) => {
    // Fresh upload — not yet bound to any saved conversation. Will be marked
    // isPersisted=true after the first sendMessageWithFile completes.
    setUploadedFile({ ...result, isPersisted: false });
    setUploadError('');
    setVirusScanError('');
  };

  const handleFileUploadError = (error, isSecurityThreat = false) => {
    if (isSecurityThreat) { setVirusScanError(error); setUploadError(''); }
    else { setUploadError(error); setVirusScanError(''); }
    setUploadedFile(null);
  };

  // ── Feature 3 & 4: textarea auto-resize + Shift+Enter handling ────────────
  const handleTextareaChange = (e) => {
    // First keystroke after the restore banner appeared = the user has
    // decided to type something new. Dismiss the banner (their typed text
    // will be persisted as the new draft on the next autosave tick).
    if (pendingDraft) setPendingDraft(null);
    setInputMessage(e.target.value);
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
  };

  // ── Restore-draft banner handlers ──────────────────────────────────────────
  const handleRestoreDraft = () => {
    if (!pendingDraft) return;
    const text = pendingDraft.text;
    setInputMessage(text);
    setPendingDraft(null);
    setTimeout(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      try { ta.setSelectionRange(text.length, text.length); } catch (_) {}
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
    }, 0);
  };

  const handleDiscardDraft = () => {
    if (!pendingDraft) return;
    setDraft(pendingDraft.id, '');
    setPendingDraft(null);
  };

  // Click handler for the Follow-Up Questions chips: insert the question into
  // the input textarea (do NOT auto-send — the clinician usually wants to
  // tweak it first), focus the textarea, and resize it to fit the new text.
  const handleFollowUpClick = (question) => {
    if (!question) return;
    setInputMessage(question);
    // Wait one tick so React has applied the controlled value before we
    // measure scrollHeight and reposition the caret.
    setTimeout(() => {
      const ta = textareaRef.current;
      if (!ta) return;
      ta.focus();
      try { ta.setSelectionRange(question.length, question.length); } catch (_) {}
      ta.style.height = 'auto';
      ta.style.height = Math.min(ta.scrollHeight, 160) + 'px';
    }, 0);
  };

  const handleTextareaKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!getCurrentSending() && inputMessage.trim()) {
        const syntheticEvent = { preventDefault: () => {} };
        if (uploadedFile?.namespace) sendMessageWithFile(syntheticEvent);
        else sendMessage(syntheticEvent);
      }
    }
    // Shift+Enter: browser default inserts a newline in <textarea>
  };

  // ── Feature 8: toggle language ─────────────────────────────────────────────
  const toggleLanguage = () => {
    setLanguage(prev => {
      const next = prev === 'en' ? 'fr' : 'en';
      setLang(next);
      return next;
    });
  };

  // ── Sending messages (unchanged logic, language-aware alerts) ──────────────
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || getCurrentSending()) return;
    if (uploadedFile?.namespace) return sendMessageWithFile(e);

    const messageText = inputMessage.trim();
    setInputMessage('');
    setPiiError('');

    const userMessage = { role: 'user', content: messageText, created_at: new Date().toISOString() };

    if (isNewChat || !currentConversation) {
      const tempConvId = 'new-chat';
      updateConversationState(tempConvId, { messages: [userMessage], sending: true, isStreaming: false, streamingMessage: '' });
      setMessages([userMessage]);
      isAtBottomRef.current = true;

      let newConversationId = null;
      let initData = null;

      try {
        initData = await conversationsAPI.initConversation(messageText);
        newConversationId = initData.id;
        const safeText = initData.safe_message || messageText;
        const safeUserMessage = { ...userMessage, content: safeText };

        const placeholderConv = {
          id: newConversationId,
          title: initData.title || TITLE_PLACEHOLDER,
          created_at: initData.created_at,
          updated_at: initData.updated_at,
          message_count: 0,
        };
        setConversations(prev => [placeholderConv, ...prev]);
        setFilteredConversations(prev => [placeholderConv, ...prev]);
        setCurrentConversation(placeholderConv);
        setIsNewChat(false);
        setMessageReferences({});

        updateConversationState(newConversationId, {
          messages: [safeUserMessage], sending: true, isStreaming: false, streamingMessage: '', references: {}
        });
        setMessages([safeUserMessage]);

        conversationsAPI.updateConversationTitle(newConversationId, safeText)
          .then(updated => {
            if (updated?.title) {
              const patch = c => c.id === newConversationId ? { ...c, title: updated.title } : c;
              setConversations(prev => prev.map(patch));
              setFilteredConversations(prev => prev.map(patch));
              setCurrentConversation(prev => prev?.id === newConversationId ? { ...prev, title: updated.title } : prev);
            }
          })
          .catch(() => {});

        let streamedText = '';
        let pendingSources = [];

        const controller = conversationsAPI.sendMessageStream(newConversationId, safeText, {
          onSources: (sources) => { pendingSources = sources; },
          onToken: (token) => {
            streamedText += token;
            updateConversationState(newConversationId, { sending: false, isStreaming: true, streamingMessage: streamedText });
          },
          onTrimTo: (length) => {
            streamedText = streamedText.slice(0, length);
            updateConversationState(newConversationId, { streamingMessage: streamedText });
          },
          onComplete: (messageId) => {
            const assistantMessage = { role: 'assistant', content: streamedText, created_at: new Date().toISOString(), id: messageId };
            const finalMessages = [safeUserMessage, assistantMessage];
            const newRefs = pendingSources.length > 0 ? { [messageId]: pendingSources } : {};
            updateConversationState(newConversationId, {
              messages: finalMessages, isStreaming: false, streamingMessage: '', sending: false, references: newRefs
            });
            isAtBottomRef.current = false;
            setMessages(finalMessages);
            if (pendingSources.length > 0) setMessageReferences(newRefs);
            loadConversations().catch(() => {});
          },
          onError: (errorMsg) => {
            console.error('SSE stream error (new conversation):', errorMsg);
            updateConversationState(newConversationId, { messages: [safeUserMessage], sending: false, isStreaming: false, streamingMessage: '' });
            setMessages([safeUserMessage]);
            setInputMessage(safeText);
          },
        }, responseMode);
        streamingIntervalsRef.current.set(newConversationId, controller);

      } catch (error) {
        console.error('Failed to initialise new conversation:', error);
        if (error.response?.status === 422) {
          const d = error.response?.data?.detail;
          if (d && (d.has_pii || typeof d === 'object'))
            setPiiError(d.message || d.detail || 'Your message contains personally identifiable information (PII) and cannot be processed.');
        }
        setInputMessage(messageText);
        setMessages([]);
        updateConversationState(tempConvId, { messages: [], sending: false, isStreaming: false, streamingMessage: '' });
        if (newConversationId) {
          setConversations(prev => prev.filter(c => c.id !== newConversationId));
          setFilteredConversations(prev => prev.filter(c => c.id !== newConversationId));
        }
      }
      return;
    }

    const conversationId = currentConversation.id;
    // `let` so onUserMessage can swap the optimistic raw bubble for the
    // DLP-masked text the backend echoes back at the start of the stream.
    let newMessages = [...messages, userMessage];
    setMessages(newMessages);
    isAtBottomRef.current = true;

    updateConversationState(conversationId, {
      messages: newMessages, sending: true, isStreaming: false, streamingMessage: '',
      references: getConversationState(conversationId).references || {},
    });

    let streamedText = '';
    let pendingSources = [];

    const controller = conversationsAPI.sendMessageStream(conversationId, messageText, {
      onUserMessage: (maskedContent) => {
        // Replace the optimistic raw bubble with the DLP-masked version.
        newMessages = [...messages, { ...userMessage, content: maskedContent }];
        updateConversationState(conversationId, { messages: newMessages });
        if (currentConversation?.id === conversationId) setMessages(newMessages);
      },
      onSources: (sources) => { pendingSources = sources; },
      onToken: (token) => {
        streamedText += token;
        updateConversationState(conversationId, { sending: false, isStreaming: true, streamingMessage: streamedText });
      },
      onTrimTo: (length) => {
        streamedText = streamedText.slice(0, length);
        updateConversationState(conversationId, { streamingMessage: streamedText });
      },
      onComplete: (messageId) => {
        const assistantMessage = { role: 'assistant', content: streamedText, created_at: new Date().toISOString(), id: messageId };
        const finalMessages = [...newMessages, assistantMessage];
        const newRefs = pendingSources.length > 0 ? { [messageId]: pendingSources } : {};
        updateConversationState(conversationId, {
          messages: finalMessages, isStreaming: false, streamingMessage: '', sending: false,
          references: { ...getConversationState(conversationId).references, ...newRefs },
        });
        if (currentConversation?.id === conversationId) {
          isAtBottomRef.current = false;
          setMessages(finalMessages);
          if (pendingSources.length > 0)
            setMessageReferences(prev => ({ ...prev, [messageId]: pendingSources }));
        }
        loadConversations().catch(() => {});
      },
      onError: (errorMsg) => {
        console.error('SSE stream error:', errorMsg);
        updateConversationState(conversationId, { messages, sending: false, isStreaming: false, streamingMessage: '' });
        if (currentConversation?.id === conversationId) {
          setMessages(messages);
          setInputMessage(messageText);
        }
      },
    }, responseMode);
    streamingIntervalsRef.current.set(conversationId, controller);
  };

  const sendMessageWithFile = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || getCurrentSending() || !uploadedFile?.namespace) return;

    const messageText = inputMessage.trim();
    setInputMessage('');
    setPiiError('');
    const userMessage = { role: 'user', content: messageText, created_at: new Date().toISOString() };

    if (isNewChat || !currentConversation) {
      const tempConvId = 'new-chat';
      updateConversationState(tempConvId, { messages: [userMessage], sending: true, isStreaming: false, streamingMessage: '' });
      setMessages([userMessage]);
      isAtBottomRef.current = true;

      let newConversationId = null;
      try {
        // Pass the attachment to /init so the backend persists it on the new
        // conversation row immediately — the row is then the source of truth
        // for future chat-switch rehydration.
        const initData = await conversationsAPI.initConversation(
          messageText, null, uploadedFile.namespace, uploadedFile.name || 'Attached Document'
        );
        newConversationId = initData.id;
        const safeText = initData.safe_message || messageText;
        const safeUserMessage = { ...userMessage, content: safeText };
        const placeholderConv = {
          id: newConversationId,
          title: initData.title || TITLE_PLACEHOLDER,
          created_at: initData.created_at,
          updated_at: initData.updated_at,
          message_count: 0,
          temp_namespace: uploadedFile.namespace,
          attachment_filename: uploadedFile.name || 'Attached Document',
        };
        setConversations(prev => [placeholderConv, ...prev]);
        setFilteredConversations(prev => [placeholderConv, ...prev]);
        setCurrentConversation(placeholderConv);
        setIsNewChat(false);
        setMessageReferences({});
        // Flip the upload to persisted now that it's bound to a saved conversation.
        setUploadedFile(prev => prev ? { ...prev, isPersisted: true } : prev);
        updateConversationState(newConversationId, { messages: [safeUserMessage], sending: true, isStreaming: false, streamingMessage: '', references: {} });
        setMessages([safeUserMessage]);

        conversationsAPI.updateConversationTitle(newConversationId, safeText)
          .then(updated => {
            if (updated?.title) {
              const patch = c => c.id === newConversationId ? { ...c, title: updated.title } : c;
              setConversations(prev => prev.map(patch));
              setFilteredConversations(prev => prev.map(patch));
              setCurrentConversation(prev => prev?.id === newConversationId ? { ...prev, title: updated.title } : prev);
            }
          }).catch(() => {});

        let streamedText = '';
        let pendingSources = [];

        const controller = conversationsAPI.sendMessageWithFileStream(
          newConversationId, safeText, uploadedFile.namespace, uploadedFile.name || 'Attached Document',
          {
            onSources: (s) => { pendingSources = s; },
            onToken: (token) => {
              streamedText += token;
              updateConversationState(newConversationId, { sending: false, isStreaming: true, streamingMessage: streamedText });
            },
            onTrimTo: (len) => {
              streamedText = streamedText.slice(0, len);
              updateConversationState(newConversationId, { streamingMessage: streamedText });
            },
            onComplete: (messageId) => {
              const assistantMessage = { role: 'assistant', content: streamedText, created_at: new Date().toISOString(), id: messageId };
              const finalMessages = [safeUserMessage, assistantMessage];
              const newRefs = pendingSources.length > 0 ? { [messageId]: pendingSources } : {};
              updateConversationState(newConversationId, { messages: finalMessages, isStreaming: false, streamingMessage: '', sending: false, references: newRefs });
              isAtBottomRef.current = false;
              setMessages(finalMessages);
              if (pendingSources.length > 0) setMessageReferences(newRefs);
              loadConversations().catch(() => {});
            },
            onError: (errorMsg) => {
              console.error('FileStream SSE error (new conv):', errorMsg);
              updateConversationState(newConversationId, { messages: [safeUserMessage], sending: false, isStreaming: false, streamingMessage: '' });
              setMessages([safeUserMessage]);
              setInputMessage(safeText);
            },
          },
          responseMode
        );
        streamingIntervalsRef.current.set(newConversationId, controller);

      } catch (error) {
        console.error('Failed to send message with file (new conv):', error);
        if (error.response?.status === 422) {
          const d = error.response?.data?.detail;
          if (d && (d.has_pii || typeof d === 'object'))
            setPiiError(d.message || d.detail || 'Your message contains personally identifiable information (PII) and cannot be processed.');
        }
        setInputMessage(messageText);
        setMessages([]);
        updateConversationState(tempConvId, { messages: [], sending: false, isStreaming: false, streamingMessage: '' });
        if (newConversationId) {
          setConversations(prev => prev.filter(c => c.id !== newConversationId));
          setFilteredConversations(prev => prev.filter(c => c.id !== newConversationId));
        }
      }
      return;
    }

    const conversationId = currentConversation.id;
    // `let` so onUserMessage can swap the optimistic raw bubble for the
    // DLP-masked text the backend echoes back at the start of the stream.
    let newMessages = [...messages, userMessage];
    setMessages(newMessages);
    isAtBottomRef.current = true;
    updateConversationState(conversationId, { messages: newMessages, sending: true, isStreaming: false, streamingMessage: '' });

    // The backend will persist this namespace on the conversation row (it may
    // be a re-attachment that replaces a previous one). Reflect that locally
    // so dropUnboundUpload doesn't treat it as deletable on the next switch.
    setUploadedFile(prev => prev ? { ...prev, isPersisted: true } : prev);
    setCurrentConversation(prev =>
      prev ? { ...prev, temp_namespace: uploadedFile.namespace, attachment_filename: uploadedFile.name || 'Attached Document' } : prev
    );

    let streamedText = '';
    let pendingSources = [];

    const controller = conversationsAPI.sendMessageWithFileStream(
      conversationId, messageText, uploadedFile.namespace, uploadedFile.name || 'Attached Document',
      {
        onUserMessage: (maskedContent) => {
          // Replace the optimistic raw bubble with the DLP-masked version.
          newMessages = [...messages, { ...userMessage, content: maskedContent }];
          updateConversationState(conversationId, { messages: newMessages });
          if (currentConversation?.id === conversationId) setMessages(newMessages);
        },
        onSources: (s) => { pendingSources = s; },
        onToken: (token) => {
          streamedText += token;
          updateConversationState(conversationId, { sending: false, isStreaming: true, streamingMessage: streamedText });
        },
        onTrimTo: (len) => {
          streamedText = streamedText.slice(0, len);
          updateConversationState(conversationId, { streamingMessage: streamedText });
        },
        onComplete: (messageId) => {
          const assistantMessage = { role: 'assistant', content: streamedText, created_at: new Date().toISOString(), id: messageId };
          const finalMessages = [...newMessages, assistantMessage];
          const newRefs = pendingSources.length > 0 ? { [messageId]: pendingSources } : {};
          updateConversationState(conversationId, {
            messages: finalMessages, isStreaming: false, streamingMessage: '', sending: false,
            references: { ...getConversationState(conversationId).references, ...newRefs },
          });
          if (currentConversation?.id === conversationId) {
            isAtBottomRef.current = false;
            setMessages(finalMessages);
            if (pendingSources.length > 0) setMessageReferences(prev => ({ ...prev, [messageId]: pendingSources }));
          }
          loadConversations().catch(() => {});
        },
        onError: (errorMsg) => {
          console.error('FileStream SSE error:', errorMsg);
          updateConversationState(conversationId, { messages, sending: false, isStreaming: false, streamingMessage: '' });
          if (currentConversation?.id === conversationId) {
            setMessages(messages);
            setInputMessage(messageText);
          }
        },
      },
      responseMode
    );
    streamingIntervalsRef.current.set(conversationId, controller);
  };

  const handleLogout = async () => {
    // Drafts are intentionally preserved across logout. Per-user namespacing
    // in services/draftStorage.js keeps each clinician's typed messages
    // isolated, so on the next sign-in the Restore banner re-offers the
    // unsent question. authAPI.logout clears the current-user pointer so
    // the next user's session doesn't accidentally inherit this user's
    // drafts namespace.
    try { await authAPI.logout(); } catch (_) {}
    window.location.replace('/login');
  };

  const handleHelp = () => {
    setUserMenuOpen(false);
    setShowHelpModal(true);
  };

  // CME Certificate modal state + handlers.
  const [cmeModalOpen, setCmeModalOpen] = useState(false);
  const [cmeDesignation, setCmeDesignation] = useState('');
  const [cmeLicense, setCmeLicense] = useState('');
  const [cmePeriodPreset, setCmePeriodPreset] = useState('all');
  const [cmePeriodFrom, setCmePeriodFrom] = useState('');
  const [cmePeriodTo, setCmePeriodTo] = useState('');
  const [cmeSubmitting, setCmeSubmitting] = useState(false);
  const [cmeError, setCmeError] = useState('');

  const handleOpenCmeModal = () => {
    setUserMenuOpen(false);
    setCmeDesignation('');
    setCmeLicense('');
    setCmePeriodPreset('all');
    setCmePeriodFrom('');
    setCmePeriodTo('');
    setCmeError('');
    setCmeSubmitting(false);
    setCmeModalOpen(true);
  };

  const handleCloseCmeModal = () => {
    if (cmeSubmitting) return;
    setCmeModalOpen(false);
  };

  // Resolve the selected preset / custom inputs into {start, end} ISO date
  // strings (YYYY-MM-DD) or null. Returns null when the user hasn't picked a
  // value for that bound — backend treats null as "open" (first message / now).
  const resolveCmePeriod = () => {
    if (cmePeriodPreset === 'all') {
      return { period_start: null, period_end: null };
    }
    if (cmePeriodPreset === 'custom') {
      return {
        period_start: cmePeriodFrom || null,
        period_end:   cmePeriodTo   || null,
      };
    }
    const days = { last_7: 7, last_30: 30, last_90: 90, last_365: 365 }[cmePeriodPreset];
    if (!days) return { period_start: null, period_end: null };
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - days);
    const iso = (d) => d.toISOString().slice(0, 10);
    return { period_start: iso(start), period_end: iso(today) };
  };

  const handleGenerateCertificate = async () => {
    if (cmeSubmitting) return;
    setCmeError('');

    const { period_start, period_end } = resolveCmePeriod();
    if (period_start && period_end && period_start > period_end) {
      setCmeError(t('cme_period_error_invalid'));
      return;
    }

    setCmeSubmitting(true);
    try {
      await certificateAPI.generate({
        professional_designation: cmeDesignation.trim() || null,
        registration_number: cmeLicense.trim() || null,
        period_start,
        period_end,
        language,
      });
      setCmeModalOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        setCmeError(t('cme_error_no_activity'));
      } else {
        setCmeError(t('cme_error_generic'));
      }
    } finally {
      setCmeSubmitting(false);
    }
  };

  const handleCollapsedNewChat = () => {
    setCollapsedSearchOpen(false);
    setCollapsedChatsOpen(false);
    createNewConversation();
  };

  const handleCollapsedConvSelect = (conversationId) => {
    setCollapsedChatsOpen(false);
    loadConversation(conversationId);
  };

  // ── Helper: build ReactMarkdown components for a given assistant message ──
  const buildMarkdownComponents = (msg, refs, query) => {
    const q = (query || '').trim();
    const qLower = q.toLowerCase();
    const wrap = (Tag) => ({ children }) => {
      const blockText = q ? childrenToString(children).toLowerCase() : '';
      const hasMatch = q && blockText.includes(qLower);
      return (
        <Tag className={hasMatch ? 'has-search-match' : undefined}>
          <CitationText messageId={msg.id} references={refs} onGetSignedUrl={getSignedUrlForTooltip} tFn={t} searchQuery={q}>
            {children}
          </CitationText>
        </Tag>
      );
    };
    return {
      p: wrap('p'), li: wrap('li'),
      h1: wrap('h1'), h2: wrap('h2'), h3: wrap('h3'),
      h4: wrap('h4'), h5: wrap('h5'), h6: wrap('h6'),
      strong: wrap('strong'), em: wrap('em'),
      blockquote: wrap('blockquote'),
      td: wrap('td'), th: wrap('th'),
    };
  };

  // Plain user-message markdown components — highlight matches inside the
  // rendered question body. Returns undefined when no query is active so React
  // falls back to ReactMarkdown's default renderers and avoids per-render work.
  const buildUserMarkdownComponents = (query) => {
    const q = (query || '').trim();
    if (!q) return undefined;
    const qLower = q.toLowerCase();
    const wrap = (Tag) => ({ children }) => {
      const blockText = childrenToString(children).toLowerCase();
      const hasMatch = blockText.includes(qLower);
      return (
        <Tag className={hasMatch ? 'has-search-match' : undefined}>
          {highlightChildren(children, q)}
        </Tag>
      );
    };
    return {
      p: wrap('p'), li: wrap('li'),
      h1: wrap('h1'), h2: wrap('h2'), h3: wrap('h3'),
      h4: wrap('h4'), h5: wrap('h5'), h6: wrap('h6'),
      strong: wrap('strong'), em: wrap('em'),
      blockquote: wrap('blockquote'),
      td: wrap('td'), th: wrap('th'),
    };
  };

  // ── Feature 1 + change #10: grouped sidebar render ────────────────────────
  // Month/year groups are rendered with a disclosure caret and start collapsed.
  const renderConversationList = (onSelect) => {
    if (loading && conversations.length === 0) return <div className="loading">{t('loading')}</div>;
    if (filteredConversations.length === 0 && searchQuery) return <div className="empty-state">{t('no_matching')}</div>;
    if (filteredConversations.length === 0) return <div className="empty-state">{t('no_conversations')}</div>;

    const groups = groupConversationsByDate(
      filteredConversations,
      {
        today: t('today'), yesterday: t('yesterday'),
        last_7_days: t('last_7_days'),
      },
      language,
    );

    const handleSelect = onSelect || loadConversation;

    return groups.map(group => {
      const isCollapsible = group.collapsible;
      // Auto-expand the month bucket when the user is searching so matches
      // inside an otherwise-collapsed month are still visible.
      const isExpanded = isCollapsible
        ? (!!expandedMonths[group.key] || !!searchQuery.trim())
        : true;
      const onLabelClick = isCollapsible
        ? () => setExpandedMonths(prev => ({ ...prev, [group.key]: !prev[group.key] }))
        : undefined;
      return (
        <div key={group.key} className={`conv-group ${isCollapsible ? 'conv-group-collapsible' : ''}`}>
          <div
            className={`conv-group-label ${isCollapsible ? 'conv-group-label-collapsible' : ''} ${isExpanded ? 'expanded' : 'collapsed'}`}
            onClick={onLabelClick}
            role={isCollapsible ? 'button' : undefined}
            tabIndex={isCollapsible ? 0 : undefined}
            onKeyDown={isCollapsible ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onLabelClick(); } } : undefined}
            aria-expanded={isCollapsible ? isExpanded : undefined}
          >
            {isCollapsible && (
              <span className="conv-group-caret" aria-hidden="true">{isExpanded ? '▼' : '▶'}</span>
            )}
            <span className="conv-group-text">{group.label}</span>
            {isCollapsible && (
              <span className="conv-group-count">{group.items.length}</span>
            )}
          </div>
          {isExpanded && group.items.map(conv => (
            <div
              key={conv.id}
              className={`conversation-item ${currentConversation?.id === conv.id ? 'active' : ''}`}
              onClick={() => handleSelect(conv.id)}
            >
              <div className="conversation-info">
                <div className="conversation-title">{highlightText(displayTitle(conv.title), searchQuery)}</div>
              </div>
              <button className="delete-btn" onClick={(e) => deleteConversation(conv.id, e)} title={t('delete')}><MdDeleteOutline size={18} /></button>
            </div>
          ))}
        </div>
      );
    });
  };

  // ── Reference section render (feature 5: collapsible) ─────────────────────
  const renderReferences = (msg) => {
    const refs = messageReferences[msg.id];
    if (!refs) return null;
    // undefined (never toggled) → collapsed by default; false → expanded; true → collapsed
    const isCollapsed = collapsedRefs[msg.id] !== false;

    return (
      <div className="references-section">
        <div className="references-header" onClick={() => toggleRefs(msg.id)}>
          <h4>{t('references')}</h4>
          <span className="refs-toggle-btn">{isCollapsed ? '▶' : '▼'} {isCollapsed ? t('show') : t('hide')}</span>
        </div>
        {!isCollapsed && (
          <div className="references-list">
            {refs.map((ref) => {
              // Mirror the tooltip's strength logic: derive from the user-facing
              // Type label, skipping the "External Reference" placeholder so
              // unclassified refs don't get a misleading badge.
              const typeLabelRaw = (ref.type?.trim()) || (ref.filetype?.trim()) || '';
              const isExternalPlaceholder = typeLabelRaw.toLowerCase() === 'external reference';
              const displayType = (typeLabelRaw && !isExternalPlaceholder) ? typeLabelRaw : '';
              const strengthKey = STRENGTH_KEY_BY_TYPE(displayType);
              const strengthText = strengthLabel(strengthKey, language);

              return (
              <div key={ref.id} className="reference-item" id={`ref-${msg.id}-${ref.id}`}>
                <div className="reference-header">
                  <span className="reference-number">[{ref.id}]</span>
                  <span
                    className={`reference-source ${ref.clickable !== false ? 'clickable-source' : 'reference-source-not-clickable'}`}
                    onClick={() => ref.clickable !== false && handleReferenceClick(ref)}
                    title={ref.clickable === false ? t('source_not_openable') : (ref.is_web_result ? t('click_open_website') : t('click_open_file'))}
                  >
                    {ref.source}
                  </span>
                </div>
                {/* Metadata badges: Type / Page / Canadian / Impact / Strength.
                    Shown for BOTH KB and GMTD refs now that the LLM emits
                    [Type: …] and [Canadian: …] tags on every AI source.
                    Page-number rows still naturally only appear for KB refs
                    (GMTD refs don't carry a page).  We keep the empty/blank
                    field guards so we never render an empty Type badge for
                    refs that came in without any classification at all. */}
                {ref.clickable !== false && (
                  (ref.type?.trim() && ref.type.trim().toLowerCase() !== 'external reference') ||
                  (ref.filetype?.trim() && ref.filetype.trim().toLowerCase() !== 'external reference') ||
                  ref.page != null ||
                  (Array.isArray(ref.pages) && ref.pages.length > 0) ||
                  (ref.canadian_guideline != null && String(ref.canadian_guideline).trim() !== '') ||
                  (ref.impact != null && String(ref.impact).toLowerCase() === 'true')
                ) && (
                  <div className="reference-metadata">
                    {((ref.type?.trim() && ref.type.trim().toLowerCase() !== 'external reference') ||
                      (ref.filetype?.trim() && ref.filetype.trim().toLowerCase() !== 'external reference')) && (
                      <div className="reference-metadata-box reference-filetype">
                        <span className="metadata-label">{t('type_label')}</span>
                        <span className="metadata-value">{localizeType(ref.type?.trim() || ref.filetype?.trim(), language)}</span>
                      </div>
                    )}
                    {((Array.isArray(ref.pages) && ref.pages.length > 0) || ref.page != null) && (
                      <div className="reference-metadata-box reference-pages">
                        <span className="metadata-label">
                          {Array.isArray(ref.pages) && ref.pages.length > 1 ? t('pages_label') : t('page_label')}
                        </span>
                        <span className="metadata-value">
                          {Array.isArray(ref.pages) && ref.pages.length > 0 ? ref.pages.join(', ') : ref.page}
                        </span>
                      </div>
                    )}
                    {ref.canadian_guideline != null && String(ref.canadian_guideline).trim() !== '' && (
                      <div className="reference-metadata-box reference-canadian">
                        <span className="metadata-label">{t('guideline_label')}</span>
                        <span className="metadata-value">{String(ref.canadian_guideline).toLowerCase() === 'true' ? t('canadian') : t('non_canadian')}</span>
                      </div>
                    )}
                    {ref.impact != null && String(ref.impact).toLowerCase() === 'true' && (
                      <div className="reference-metadata-box reference-impact">
                        <span className="metadata-label">{t('journal_label')}</span>
                        <span className="metadata-value">{t('reputable_journal')}</span>
                      </div>
                    )}
                    {strengthText && (
                      <div className={`reference-metadata-box reference-strength reference-strength-${strengthKey}`}>
                        <span className="metadata-label">{t('strength_label')}</span>
                        <span className="metadata-value">{strengthText}</span>
                      </div>
                    )}
                  </div>
                )}
                {(ref.metadata_tag?.trim() || (ref.clickable !== false && ref.is_web_result && (ref.filetype === 'External Reference' || ref.filetype === '')) || ref.clickable === false) && (
                  <div className="reference-metadata">
                    <div className="reference-metadata-box reference-metadata-tag">
                      <span className="metadata-label">{t('source_label')}</span>
                      <span className="metadata-value">{ref.metadata_tag?.trim() ? localizeMetaTag(ref.metadata_tag.trim(), language) : t('ai_source')}</span>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className={`chat-container ${mobileNavOpen ? 'mobile-nav-open' : ''}`}>
      {/* Phone-only backdrop behind the slide-in sidebar drawer */}
      <div
        className="sidebar-backdrop"
        onClick={closeMobileNav}
        aria-hidden="true"
      />
      {/* Sidebar */}
      <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileNavOpen ? 'mobile-open' : ''}`}>
        {sidebarCollapsed ? (
          <>
            {/* Collapsed view */}
            <div className="sidebar-collapsed-top">
              <button
                className="sidebar-icon-btn"
                onClick={() => setSidebarCollapsed(false)}
                title={t('expand_sidebar')}
                aria-label={t('expand_sidebar')}
              >
                <GoSidebarCollapse size={20} />
              </button>
              <button
                className="sidebar-icon-btn"
                onClick={handleCollapsedNewChat}
                title={t('new_chat')}
                aria-label={t('new_chat')}
              >
                <RiChatNewLine size={20} />
              </button>
              <div className="collapsed-popover-anchor" ref={collapsedSearchRef}>
                <button
                  className={`sidebar-icon-btn ${collapsedSearchOpen ? 'active' : ''}`}
                  onClick={() => {
                    setCollapsedChatsOpen(false);
                    setCollapsedSearchOpen(v => !v);
                  }}
                  title={t('search_placeholder')}
                  aria-label={t('search_placeholder')}
                >
                  <IoSearchSharp size={20} />
                </button>
                {collapsedSearchOpen && (
                  <div className="collapsed-search-popover">
                    <div className="search-input-wrapper">
                      <IoSearchSharp size={15} className="search-icon" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t('search_placeholder')}
                        className="search-input"
                        autoFocus
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          className="search-clear-btn"
                          onClick={() => setSearchQuery('')}
                          aria-label="Clear search"
                          title="Clear search"
                        >
                          <RxCross2 size={14} />
                        </button>
                      )}
                    </div>
                    {searchQuery.trim() && (
                      <div className="collapsed-search-results">
                        {filteredConversations.length === 0 ? (
                          <div className="empty-state">{t('no_matching')}</div>
                        ) : (
                          filteredConversations.map(conv => (
                            <div
                              key={conv.id}
                              className={`conversation-item ${currentConversation?.id === conv.id ? 'active' : ''}`}
                              onClick={() => {
                                setCollapsedSearchOpen(false);
                                loadConversation(conv.id);
                              }}
                            >
                              <div className="conversation-info">
                                <div className="conversation-title">{highlightText(displayTitle(conv.title), searchQuery)}</div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="collapsed-popover-anchor" ref={collapsedChatsRef}>
                <button
                  className={`sidebar-icon-btn ${collapsedChatsOpen ? 'active' : ''}`}
                  onClick={() => {
                    setCollapsedSearchOpen(false);
                    setCollapsedChatsOpen(v => !v);
                  }}
                  title={t('recent_chats')}
                  aria-label={t('recent_chats')}
                >
                  <PiChatsThin size={22} />
                </button>
                {collapsedChatsOpen && (
                  <div className="collapsed-chats-popover">
                    <div className="collapsed-chats-header">{t('chats')}</div>
                    <div className="collapsed-chats-list">
                      {renderConversationList(handleCollapsedConvSelect)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="sidebar-collapsed-bottom" ref={userMenuRef}>
              <button
                className={`sidebar-icon-btn user-avatar-btn ${userMenuOpen ? 'active' : ''}`}
                onClick={() => setUserMenuOpen(v => !v)}
                title={user?.username || t('open_user_menu')}
                aria-label={t('open_user_menu')}
              >
                <UserIcon width={20} height={20} />
              </button>
              {userMenuOpen && (
                <div className="user-menu user-menu-collapsed">
                  <div className="user-menu-header">
                    <UserIcon width={16} height={16} className="user-footer-icon" />
                    <span>{user?.username}</span>
                  </div>
                  <button onClick={handleOpenCmeModal} className="user-menu-item">
                    <MdOutlineWorkspacePremium size={16} />
                    {t('cme_certificate')}
                  </button>
                  <button onClick={handleHelp} className="user-menu-item">
                    <MdOutlineHelpOutline size={16} />
                    {t('help')}
                  </button>
                  <button onClick={handleLogout} className="user-menu-item">
                    <CiLogout size={16} />
                    {t('logout')}
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Top bar: language toggle + Admin + Collapse */}
            <div className="sidebar-topbar">
              <LanguageToggle language={language} onToggle={toggleLanguage} />
              {user?.is_admin && (
                <button
                  className="admin-btn"
                  onClick={() => navigate('/admin')}
                  title="Open admin reports"
                >
                  Admin
                </button>
              )}
              <button
                className="sidebar-collapse-btn"
                onClick={() => setSidebarCollapsed(true)}
                title={t('collapse_sidebar')}
                aria-label={t('collapse_sidebar')}
              >
                <GoSidebarExpand size={18} />
              </button>
            </div>

            <div className="new-chat-section">
              <button onClick={createNewConversation} className="new-chat-btn" title={t('new_chat')}>
                <RiChatNewLine size={16} />
                {t('new_chat')}
              </button>
            </div>

            <div className="search-container">
              <div className="search-input-wrapper">
                <IoSearchSharp size={15} className="search-icon" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('search_placeholder')}
                  className="search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="search-clear-btn"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                    title="Clear search"
                  >
                    <RxCross2 size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="sidebar-header">
              <h2>
                <PiChatsThin size={20} className="sidebar-chat-icon" />
                {t('chats')}
              </h2>
            </div>

            {/* Feature 1: Grouped conversation history */}
            <div className="conversations-list">
              {renderConversationList()}
            </div>

            <div className="sidebar-footer" ref={userMenuRef}>
              <button
                type="button"
                className="user-info user-info-btn"
                onClick={() => setUserMenuOpen(v => !v)}
                title={t('open_user_menu')}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
              >
                <UserIcon width={18} height={18} className="user-footer-icon" />
                <span>{user?.username}</span>
              </button>
              {userMenuOpen && (
                <div className="user-menu user-menu-expanded">
                  <button onClick={handleOpenCmeModal} className="user-menu-item">
                    <MdOutlineWorkspacePremium size={16} />
                    {t('cme_certificate')}
                  </button>
                  <button onClick={handleHelp} className="user-menu-item">
                    <MdOutlineHelpOutline size={16} />
                    {t('help')}
                  </button>
                  <button onClick={handleLogout} className="user-menu-item">
                    <CiLogout size={16} />
                    {t('logout')}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Main Chat Area */}
      <div className="chat-main">
        <div className="chat-header">
          <button
            type="button"
            className="mobile-nav-toggle"
            onClick={openMobileNav}
            title={t('recent_chats')}
            aria-label={t('recent_chats')}
          >
            <RiMenuLine size={22} />
          </button>
          <div className="header-title-section">
            {/* Change #9: Download button on top, then title, then date of last asked question below */}
            {getCurrentMessages().length > 0 && (
              <div className="header-download-row" ref={downloadMenuRef}>
                <button
                  className="download-inline-btn"
                  onClick={() => setShowDownloadMenu(v => !v)}
                  title={t('download')}
                >
                  <DownloadArrowIcon width={20} height={20} />
                </button>
                {showDownloadMenu && (
                  <div className="download-menu-dropdown">
                    <button onClick={handleDownloadTxt}>{t('download_txt')}</button>
                    <button onClick={handleDownloadPdf}>{t('download_pdf')}</button>
                  </div>
                )}
              </div>
            )}
            <h1>{APP_TITLE}</h1>
            {/* No subtitle when there is neither an open conversation nor a new
                chat — the row is dropped rather than left empty so the header
                doesn't keep a blank line where the prompt used to be. */}
            {(currentConversation || isNewChat) && (
              <div className="header-subtitle-row">
                <p>{currentConversation ? displayTitle(currentConversation.title) : t('new_chat')}</p>
              </div>
            )}
            {(() => {
              // Change #9: date of the last question asked in this chat. The
              // exports reuse the same helper so they never show today's date.
              const formatted = getChatDateLabel();
              if (!formatted) return null;
              return <p className="header-chat-date">{formatted}</p>;
            })()}
          </div>
          <div className="header-banners">
            <div className="header-banner warning"><IoWarningOutline size={14} color="#4B2A6D" style={{marginRight: 5, flexShrink: 0}} />{t('phi_warning')}</div>
            <div className="header-banner disclaimer">{t('disclaimer')}</div>
            <button
              type="button"
              className="chat-help-btn"
              onClick={() => setShowHelpModal(true)}
              title={t('help')}
            >
              {t('help')}
            </button>
          </div>
        </div>

        <HelpFeedbackModal
          open={showHelpModal}
          onClose={() => setShowHelpModal(false)}
          lockedEmail={user?.email || ''}
          language={language}
        />

        {/* Feature 2: track scroll position to avoid unwanted auto-scroll */}
        <div
          className="messages-container"
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
        >
          {getCurrentMessages().length === 0 ? (
            <div className="empty-chat"><h2>{t('welcome')}</h2></div>
          ) : (
            getCurrentMessages().map((msg, index) => {
              const refs = messageReferences[msg.id] || null;
              return (
                <div key={index} className={`message ${msg.role}`}>
                  <div className={`message-avatar ${msg.role}`}>
                    {msg.role === 'user'
                      ? <UserIcon width={20} height={20} />
                      : <RiRobot3Line size={20} color="#FFFFFF" />}
                  </div>
                  <div className="message-content">
                    <div className="message-text" data-msg-id={msg.id}>
                      {msg.role === 'assistant' ? (
                        <AssistantContent
                          text={msg.content}
                          components={buildMarkdownComponents(msg, refs, searchQuery)}
                          heading={t('key_points')}
                          followUpHeading={t('follow_up_questions')}
                          onFollowUpClick={handleFollowUpClick}
                          isFinal={true}
                        />
                      ) : (
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={buildUserMarkdownComponents(searchQuery)}>{msg.content}</ReactMarkdown>
                      )}
                    </div>

                    {/* Disclaimer appended as fixed text above references */}
                    {msg.role === 'assistant' && msg.id && (
                      <p className="medical-disclaimer">
                        <IoWarningOutline size={13} className="disclaimer-icon" />
                        {t('medical_disclaimer')}
                      </p>
                    )}

                    {/* Feature 5: collapsible references */}
                    {msg.role === 'assistant' && msg.id && messageReferences[msg.id] && renderReferences(msg)}

                    {msg.role === 'assistant' && (
                      <div className="message-actions">
                        <div className="feedback-buttons">
                          <button
                            className={`feedback-btn ${feedbackGiven[msg.id] === 'positive' ? 'active' : ''}`}
                            onClick={() => handleFeedbackClick(msg.id, 'positive')}
                            title={feedbackGiven[msg.id] === 'positive' ? t('remove_helpful') : t('helpful')}
                            disabled={!msg.id}
                          ><SlLike size={14} /></button>
                          <button
                            className={`feedback-btn ${feedbackGiven[msg.id] === 'negative' ? 'active' : ''}`}
                            onClick={() => handleFeedbackClick(msg.id, 'negative')}
                            title={feedbackGiven[msg.id] === 'negative' ? t('remove_not_helpful') : t('not_helpful')}
                            disabled={!msg.id}
                          ><SlDislike size={14} /></button>

                          {/* Feature 7: copy button per message */}
                          {msg.id && (
                            <button
                              className={`copy-msg-btn ${copiedMsg === msg.id ? 'copied' : ''}`}
                              onClick={() => handleCopyMessage(msg)}
                              title={t('copy')}
                            >
                              {copiedMsg === msg.id ? <FaCheck size={13} color="#2B8000" /> : <FaRegCopy size={13} />}
                            </button>
                          )}
                        </div>
                        {feedbackGiven[msg.id] && (
                          <span className="feedback-thanks">
                            {feedbackGiven[msg.id] === 'positive' ? t('feedback_thanks_positive') : t('feedback_thanks_negative')}
                          </span>
                        )}
                        {!msg.id && <span className="feedback-disabled">{t('feedback_not_available')}</span>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}

          {getCurrentIsStreaming() && getCurrentStreamingMessage() && (
            <div className="message assistant">
              <div className="message-avatar assistant"><RiRobot3Line size={20} color="#FFFFFF" /></div>
              <div className="message-content">
                <div className="message-text">
                  <AssistantContent
                    text={getCurrentStreamingMessage()}
                    heading={t('key_points')}
                    followUpHeading={t('follow_up_questions')}
                    onFollowUpClick={handleFollowUpClick}
                    isFinal={false}
                  />
                </div>
              </div>
            </div>
          )}
          {getCurrentSending() && !getCurrentStreamingMessage() && (
            <div className="message assistant">
              <div className="message-avatar assistant"><RiRobot3Line size={20} color="#FFFFFF" /></div>
              <div className="message-content">
                <div className="typing-indicator"><span></span><span></span><span></span></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="input-container">
          {virusScanError && (
            <div className="virus-scan-error-banner">
              <div className="virus-scan-error-content">
                <span className="virus-scan-error-icon">🛡️</span>
                <div className="virus-scan-error-body">
                  <strong className="virus-scan-error-title">Security Threat Detected</strong>
                  <span className="virus-scan-error-text">{virusScanError}</span>
                  <span className="virus-scan-error-hint">You can still ask questions using the chat below — no file is required.</span>
                </div>
                <button className="virus-scan-error-close" onClick={() => setVirusScanError('')} title={t('dismiss')}>✕</button>
              </div>
            </div>
          )}
          {uploadError && (
            <div className="upload-error-banner">
              <div className="upload-error-content">
                <span className="upload-error-icon">❌</span>
                <span className="upload-error-text">{uploadError}</span>
                <button className="upload-error-close" onClick={() => setUploadError('')} title={t('close')}>✕</button>
              </div>
            </div>
          )}
          {piiError && (
            <div className="pii-error-banner">
              <div className="pii-error-content">
                <span className="pii-error-icon">🚫</span>
                <span className="pii-error-text">{piiError}</span>
                <button className="pii-error-close" onClick={() => setPiiError('')} title={t('close')}>✕</button>
              </div>
            </div>
          )}

          {/* FileUpload rendered outside query-box so its fixed modal is never clipped */}
          <div className="file-upload-hidden">
            <FileUpload
              ref={fileUploadRef}
              onUploadSuccess={handleFileUploadSuccess}
              onUploadError={handleFileUploadError}
              disabled={getCurrentSending()}
              uploadedFile={uploadedFile}
              onClearFile={clearUploadedFile}
            />
          </div>

          {/* Restore-draft banner — shown when the active conversation has a
              previously typed-but-unsent message saved in localStorage and the
              textarea is currently empty (e.g. after an idle-timeout sign-out). */}
          {pendingDraft && (
            <div className="draft-restore-banner" role="status">
              <div className="draft-restore-content">
                <span className="draft-restore-icon" aria-hidden="true">💬</span>
                <div className="draft-restore-text">
                  <div className="draft-restore-prompt">{t('draft_restore_prompt')}</div>
                  <div className="draft-restore-preview" title={pendingDraft.text}>
                    “{pendingDraft.text.length > 120
                      ? pendingDraft.text.slice(0, 120) + '…'
                      : pendingDraft.text}”
                  </div>
                </div>
                <div className="draft-restore-actions">
                  <button
                    type="button"
                    className="draft-restore-btn draft-restore-btn-primary"
                    onClick={handleRestoreDraft}
                  >
                    {t('draft_restore_restore')}
                  </button>
                  <button
                    type="button"
                    className="draft-restore-btn draft-restore-btn-secondary"
                    onClick={handleDiscardDraft}
                  >
                    {t('draft_restore_discard')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Redesigned query box: Attach | Textarea | Response Mode | Send */}
          <form onSubmit={uploadedFile ? sendMessageWithFile : sendMessage} className="input-form">

            <div className="query-box">

              {/* Attach button — opens the FileUpload modal */}
              <button
                type="button"
                className={`attach-btn ${uploadedFile ? 'has-file' : ''}`}
                onClick={() => fileUploadRef.current?.triggerModal()}
                disabled={getCurrentSending()}
                title={uploadedFile ? `${t('file_label')}: ${uploadedFile.name}` : t('attach_file')}
              >
                <IoIosAttach size={22} color={uploadedFile ? '#4B2A6D' : '#4B2A6D'} />
                {uploadedFile && <span className="attach-dot" />}
              </button>

              {/* Textarea */}
              <textarea
                ref={textareaRef}
                value={inputMessage}
                onChange={handleTextareaChange}
                onKeyDown={handleTextareaKeyDown}
                placeholder={uploadedFile ? t('input_placeholder_file') : t('input_placeholder')}
                disabled={getCurrentSending()}
                className="query-textarea"
                rows={1}
              />

              {/* Response mode selector */}
              <div className="response-mode-selector" ref={responseModeRef}>
                <button
                  type="button"
                  className="response-mode-btn"
                  onClick={() => setShowResponseMenu(v => !v)}
                  title={t('response_style')}
                >
                  <span className="response-mode-label">
                    {responseMode === 'quick_read' ? t('quick_read') : t('detailed')}
                  </span>
                  <span className="response-mode-chevron">▾</span>
                </button>
                {showResponseMenu && (
                  <div className="response-mode-dropdown">
                    <button
                      type="button"
                      className={`response-mode-option ${responseMode === 'detailed' ? 'active' : ''}`}
                      onClick={() => { setResponseMode('detailed'); setShowResponseMenu(false); }}
                    >
                      {t('detailed')}
                    </button>
                    <button
                      type="button"
                      className={`response-mode-option ${responseMode === 'quick_read' ? 'active' : ''}`}
                      onClick={() => { setResponseMode('quick_read'); setShowResponseMenu(false); }}
                    >
                      {t('quick_read')}
                    </button>
                  </div>
                )}
              </div>

              {/* Send button */}
              <button
                type="submit"
                disabled={getCurrentSending() || !inputMessage.trim()}
                className="send-btn-icon"
                title={t('send')}
              >
                {getCurrentSending()
                  ? <CgSandClock size={20} color="#4B2A6D" />
                  : <FiSend size={20} color="#4B2A6D" />
                }
              </button>

            </div>
          </form>
        </div>
      </div>

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="feedback-modal-overlay" onClick={closeFeedbackModal}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h3>{t('provide_feedback')}</h3>
              <button className="modal-close-btn" onClick={closeFeedbackModal}>✕</button>
            </div>
            <div className="feedback-modal-body">
              <p>{t('feedback_prompt')}</p>
              <textarea
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                placeholder={t('feedback_placeholder')}
                className="feedback-textarea"
                rows={4}
                maxLength={1000}
              />
              <div className="character-count">{feedbackComment.length}/1000</div>
            </div>
            <div className="feedback-modal-footer">
              <button className="btn-cancel" onClick={closeFeedbackModal}>{t('cancel')}</button>
              <button className="btn-submit" onClick={handleFeedbackModalSubmit}>{t('submit_feedback')}</button>
            </div>
          </div>
        </div>
      )}

      {/* CME Certificate Modal */}
      {cmeModalOpen && (
        <div className="feedback-modal-overlay" onClick={handleCloseCmeModal}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h3>{t('cme_modal_title')}</h3>
              <button className="modal-close-btn" onClick={handleCloseCmeModal} disabled={cmeSubmitting}>✕</button>
            </div>
            <div className="feedback-modal-body">
              <p>{t('cme_modal_blurb')}</p>
              <label className="cme-field-label">
                {t('cme_professional_designation')}
                <span className="cme-optional-hint"> ({t('cme_optional_hint')})</span>
              </label>
              <input
                type="text"
                value={cmeDesignation}
                onChange={(e) => setCmeDesignation(e.target.value)}
                className="cme-field-input"
                maxLength={120}
                disabled={cmeSubmitting}
              />
              <label className="cme-field-label">
                {t('cme_registration_number')}
                <span className="cme-optional-hint"> ({t('cme_optional_hint')})</span>
              </label>
              <input
                type="text"
                value={cmeLicense}
                onChange={(e) => setCmeLicense(e.target.value)}
                className="cme-field-input"
                maxLength={80}
                disabled={cmeSubmitting}
              />
              <label className="cme-field-label">
                {t('cme_period_label')}
              </label>
              <p className="cme-period-hint">{t('cme_period_hint')}</p>
              <select
                value={cmePeriodPreset}
                onChange={(e) => setCmePeriodPreset(e.target.value)}
                className="cme-field-input"
                disabled={cmeSubmitting}
              >
                <option value="all">{t('cme_period_all')}</option>
                <option value="last_7">{t('cme_period_last_7')}</option>
                <option value="last_30">{t('cme_period_last_30')}</option>
                <option value="last_90">{t('cme_period_last_90')}</option>
                <option value="last_365">{t('cme_period_last_365')}</option>
                <option value="custom">{t('cme_period_custom')}</option>
              </select>
              {cmePeriodPreset === 'custom' && (
                <div className="cme-period-range">
                  <label className="cme-period-range-field">
                    <span>{t('cme_period_from')}</span>
                    <input
                      type="date"
                      value={cmePeriodFrom}
                      onChange={(e) => setCmePeriodFrom(e.target.value)}
                      className="cme-field-input"
                      disabled={cmeSubmitting}
                    />
                  </label>
                  <label className="cme-period-range-field">
                    <span>{t('cme_period_to')}</span>
                    <input
                      type="date"
                      value={cmePeriodTo}
                      onChange={(e) => setCmePeriodTo(e.target.value)}
                      className="cme-field-input"
                      disabled={cmeSubmitting}
                    />
                  </label>
                </div>
              )}
              {cmeError && <div className="cme-field-error">{cmeError}</div>}
            </div>
            <div className="feedback-modal-footer">
              <button className="btn-cancel" onClick={handleCloseCmeModal} disabled={cmeSubmitting}>{t('cancel')}</button>
              <button className="btn-submit" onClick={handleGenerateCertificate} disabled={cmeSubmitting}>
                {cmeSubmitting ? t('cme_generating') : t('cme_generate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Chat;
